import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { API, PUBLIC_APP_ORIGIN, apiJson, apiList } from './api';

import {
  PRESETS, DEFAULT_RULES_BANK, DEFAULT_GENERIC_RULES, DEFAULT_FORM, STEPS,
} from './constants/competition';
import {
  DEFAULT_MEMBERSHIP_TYPE_OPTIONS, DEFAULT_PLAYER_TYPE_OPTIONS,
} from './constants/membership';
import { escapeHtml, formatOrdinalDate, fmtTime12, fmtDate, fmtLabel, mtLabel, ttLabel, toDateInputValue } from './utils/format';
import { normalizeRulesList, mergeCompDefaults } from './utils/rules';
import {
  isNonMemberGuestMembershipType, normalizeMembershipFormConfig,
  getMembershipRenewalStatus, getMembershipStatusLabel, isLifeMembershipType,
  normalizeAllowedMembershipTypes,
} from './utils/membership';
import { applyClubTheme } from './utils/theme';
import { MONTH_NAMES, currentSeason, seasonLabel, seasonOf, seasonStartMonthOf } from './utils/season';
import TrophySvg from './components/TrophySvg';
import MobileNav from './components/MobileNav';
import EventsView from './components/EventsView';
import ScoreModal from './components/ScoreModal';
import ClubLeaderboardTable from './components/ClubLeaderboardTable';
import AnalyticsPanel from './components/AnalyticsPanel';
import SetupScreen from './components/SetupScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import CompRegistrationButton from './components/CompRegistrationButton';
import AdminCompRegistrationBar from './components/AdminCompRegistrationBar';
import ClubAnalyticsPanel from './components/ClubAnalyticsPanel';
import LeaderboardPanel from './components/LeaderboardPanel';
import CompRegistrationFlow from './components/CompRegistrationFlow';
import PendingRegsModal from './components/PendingRegsModal';
import AuthScreen from './components/AuthScreen';
import MembershipPortalScreen from './components/MembershipPortalScreen';
import AdminUsersPanel from './components/AdminUsersPanel';
import HelpView from './components/HelpView';
import NewCompView from './components/NewCompView';
import DashboardView from './components/DashboardView';
import MemberIdView from './components/MemberIdView';
import ClubTeamsView from './components/ClubTeamsView';
import ClubPlayersView from './components/ClubPlayersView';
import CompetitionRulesView from './components/CompetitionRulesView';
import CompetitionScores from './components/CompetitionScores';
import CompetitionFixtures from './components/CompetitionFixtures';
import CompetitionEmailPanel from './components/CompetitionEmailPanel';
import CompetitionTeams from './components/CompetitionTeams';
import CompetitionPlayerEmailPanel from './components/CompetitionPlayerEmailPanel';
import CompetitionPlayers from './components/CompetitionPlayers';
import TeamProfileContent from './components/TeamProfileContent';
import PlayerProfileContent from './components/PlayerProfileContent';
import CompetitionHome from './components/CompetitionHome';
import CompetitionSlideshow from './components/CompetitionSlideshow';
import CompetitionSettingsFields from './components/CompetitionSettingsFields';
import PlayerHomeView from './components/PlayerHomeView';
import ClubScreenView from './components/ClubScreenView';
import ClubScreenControl, { loadScreenSettings } from './components/ClubScreenControl';
import { Field, Section, Toggle } from './components/ui';


/* ====================================================================
   MAIN APP
   ==================================================================== */
function App() {
  /* --- Auth State --- */
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('greensheet_token'));
  const [currentUser, setCurrentUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // login | register | setup
  const [authLoading, setAuthLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Registration flow
  const [regCompId, setRegCompId] = useState(null); // from URL /register/:compId
  const [resetToken, setResetToken] = useState(null); // from URL /reset-password/:token
  const [membershipRoute, setMembershipRoute] = useState(null); // { clubRef, intent }
  const [compRegistrations, setCompRegistrations] = useState([]); // admin panel
  const [myRegistrations, setMyRegistrations] = useState({}); // { compId: { id, status } }
  /* The competitions the club has you down for, which is not the same as the
     ones you registered for: an admin can enter somebody directly, and an
     approved registration leaves a player-list entry behind. */
  const [myCompetitionIds, setMyCompetitionIds] = useState([]);

  const isAdmin = currentUser && (currentUser.role === 'club-admin' || currentUser.role === 'super-admin');
  const isSuperAdmin = currentUser && currentUser.role === 'super-admin';
    const shouldShowApplyMembershipButton = !!(
      currentUser &&
      currentUser.ClubId &&
      !isNonMemberGuestMembershipType(currentUser.membershipType) &&
      String(currentUser.membershipApprovalStatus || '').toLowerCase() !== 'pending' &&
      !currentUser.membershipApprovedAt
    );
  /* A life membership does not run out, so nobody is ever asked to renew
     one — even if an old due date is still on the record. */
  const shouldShowRenewButton = !!(currentUser && currentUser.ClubId && currentUser.membershipRenewalDueAt
    && !isLifeMembershipType(currentUser.membershipType));
  const renewButtonDueDate = shouldShowRenewButton ? new Date(currentUser.membershipRenewalDueAt) : null;
  const renewButtonIsDue = !!(renewButtonDueDate && renewButtonDueDate <= new Date());
  const isLoggedIn = !!currentUser;

  /** Login */
  const doLogin = async (email, password) => {
    const r = await fetch(`${API}/api/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Login failed');
    localStorage.setItem('greensheet_token', data.token);
    setAuthToken(data.token);
    setCurrentUser(data.user);
    return data;
  };

  /** Register */
  const doRegister = async (name, email, password, clubId, membershipData, membershipIntent = 'apply') => {
    const r = await fetch(`${API}/api/auth/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        email,
        password,
        clubId: clubId || undefined,
        membershipData: membershipData || {},
        membershipIntent,
      }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Registration failed');
    /* The club already had a profile for this email, so a link has gone to
       that inbox instead of signing anybody in. The screen says so. */
    if (data.claimPending) return data;
    localStorage.setItem('greensheet_token', data.token);
    setAuthToken(data.token);
    setCurrentUser(data.user);
    return data;
  };

  /** Seed first admin */
  const doSeedAdmin = async (name, email, password, clubName) => {
    const r = await fetch(`${API}/api/auth/seed-admin`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, clubName }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Setup failed');
    localStorage.setItem('greensheet_token', data.token);
    setAuthToken(data.token);
    setCurrentUser(data.user);
    setNeedsSetup(false);
    return data;
  };

  /** Logout */
  /* Competitions and Results both live on the club overview, and the phone
     bar sends you there for either. This says which part was asked for so
     the page can scroll to it instead of dumping you at the top twice. */
  const [overviewAnchor, setOverviewAnchor] = useState(null);

  const openOverview = anchor => { setOverviewAnchor(anchor || null); setViewTracked('dashboard'); };

  const doLogout = () => {
    localStorage.removeItem('greensheet_token');
    setAuthToken(null);
    setCurrentUser(null);
  };

  /** On mount: check token validity and detect first-run setup */
  useEffect(() => {
    (async () => {
      /* Does this site still need its first admin?
         A plain read. This used to POST to seed-admin with "__check__" in
         every field and infer the answer from the error that came back,
         which on a brand new site created a super-admin with "__check__"
         as its password and then left the real setup form unable to
         proceed. See GET /api/auth/setup-needed in server/routes/auth.js.

         An unreachable or unhappy server means no setup screen. Showing it
         on a failed request would offer to create a second first-admin to
         everybody whose connection dropped on the way in. */
      try {
        const r = await fetch(`${API}/api/auth/setup-needed`);
        const data = r.ok ? await r.json() : null;
        setNeedsSetup(!!(data && data.needsSetup));
      } catch {
        setNeedsSetup(false);
      }

      // Validate stored token
      const token = localStorage.getItem('greensheet_token');
      if (token) {
        try {
          const r = await fetch(`${API}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (r.ok) {
            const data = await r.json();
            setCurrentUser(data.user);
          } else {
            localStorage.removeItem('greensheet_token');
            setAuthToken(null);
          }
        } catch {
          localStorage.removeItem('greensheet_token');
          setAuthToken(null);
        }
      }

      // Check for /register/:compId URL parameter
      const path = window.location.pathname;
      const regMatch = path.match(/\/register\/(\d+)/);
      if (regMatch) setRegCompId(parseInt(regMatch[1]));

      // Check for /reset-password/:token URL parameter
      const resetMatch = path.match(/\/reset-password\/([a-f0-9]+)/);
      if (resetMatch) setResetToken(resetMatch[1]);

      // Check for /membership/:clubRef or /membership/:clubRef/renew
      const membershipMatch = path.match(/\/membership\/([^/]+)(?:\/(renew))?$/);
      if (membershipMatch) {
        const clubRef = decodeURIComponent(membershipMatch[1] || '').trim();
        if (!clubRef) {
          setAuthLoading(false);
          return;
        }
        setMembershipRoute({
          clubRef,
          intent: membershipMatch[2] === 'renew' ? 'renew' : 'apply',
        });
      }

      setAuthLoading(false);
    })();
  }, []); // eslint-disable-line

  /* --- State --- */
  const [users, setUsers] = useState([]);
  const [comps, setComps] = useState([]);
  // 'home' is the player home and the app's front door; 'dashboard' is the
  // club-wide overview, now somewhere you go rather than where you land.
  const [view, setView] = useState('home'); // home | dashboard | newComp | competition | playerProfile | teamProfile
  const [previousView, setPreviousView] = useState('home');
  const [showSidebar, setShowSidebar] = useState(false);
  const viewRef = useRef('dashboard');
  const pendingHashRestore = useRef(null); // deferred hash restore until comps are loaded

  // Build a URL hash from the current view + context
  const buildHash = (v, extra) => {
    if (v === 'competition' && extra) return `#comp/${extra}`;
    if (v === 'competitionRules') return '#competition-rules';
    if (v === 'clubPlayers') return '#players';
    if (v === 'clubTeams') return '#teams';
    if (v === 'adminUsers') return '#admin';
    if (v === 'help') return '#help';
    if (v === 'clubStats') return '#stats';
    if (v === 'memberId') return '#member-id';
    if (v === 'dashboard') return '#';
    return '#';
  };

  const setViewTracked = (newView) => {
    setPreviousView(viewRef.current);
    viewRef.current = newView;
    setView(newView);
    // Push browser history with hash for bookmarkable / refreshable navigation
    const hash = buildHash(newView).replace('#', '');
    window.history.pushState({ view: newView }, '', hash ? `#${hash}` : window.location.pathname);
  };

  // Handle browser back button — go to previous in-app view instead of leaving the app
  useEffect(() => {
    // Seed initial history state
    window.history.replaceState({ view: 'home' }, '');
    const onPopState = (e) => {
      if (e.state && e.state.view) {
        viewRef.current = e.state.view;
        setView(e.state.view);
      } else {
        viewRef.current = 'dashboard';
        setView('home');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const [selectedComp, setSelectedComp] = useState(null);
  const [compPlayers, setCompPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [competitionHomeLeaderboardMinimized, setCompetitionHomeLeaderboardMinimized] = useState(false);
  const [activeStep, setActiveStep] = useState('players');
  const [showSlideshow, setShowSlideshow] = useState(false);
  /* null until somebody picks one, and then the current season is shown —
     which depends on the club's season start, not known until the club has
     loaded. See compYearFilter, worked out below once headerClub exists. */
  const [compYearChoice, setCompYearFilter] = useState(null);
  const [competitionEmailTemplates, setCompetitionEmailTemplates] = useState([]);
  const [selectedCompPlayerIds, setSelectedCompPlayerIds] = useState([]);
  const [compEmailMode, setCompEmailMode] = useState('general'); // general | entry-confirmation
  const [compGeneralSendMode, setCompGeneralSendMode] = useState('template'); // template | quick
  const [compGeneralTemplateId, setCompGeneralTemplateId] = useState('');
  const [compQuickEmailSubject, setCompQuickEmailSubject] = useState('');
  const [compQuickEmailBody, setCompQuickEmailBody] = useState('');
  const [sendingCompEmail, setSendingCompEmail] = useState(false);

  // Player profile
  const [playerProfile, setPlayerProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwValue, setResetPwValue] = useState('');

  // New competition wizard
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [compForm, setCompForm] = useState({ ...mergeCompDefaults(), startDate: new Date().toISOString().slice(0, 10) });
  const [showCustomSettings, setShowCustomSettings] = useState(true);
  const endDateTouched = useRef(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [clubCompDefaults, setClubCompDefaults] = useState(null);
  const [siteCompDefaults, setSiteCompDefaults] = useState(null);
  const [rulesLibrary, setRulesLibrary] = useState({ generic: DEFAULT_GENERIC_RULES, custom: [] });
  const [rulesPickerContext, setRulesPickerContext] = useState('new'); // new | edit
  const [showRulesPreviewModal, setShowRulesPreviewModal] = useState(false);
  const [homeRuleTitle, setHomeRuleTitle] = useState('');
  const [homeRuleDetail, setHomeRuleDetail] = useState('');
  const [savingHomeRules, setSavingHomeRules] = useState(false);

  // Manual fixture draw
  const [fixtureMode, setFixtureMode] = useState('auto'); // 'auto' | 'manual'
  const [manualMatch, setManualMatch] = useState({ round: 1, homeTeamId: '', awayTeamId: '', rink: '', green: '', time: '' });
  // For locking time input if round already has matches
  const [manualTimeLocked, setManualTimeLocked] = useState(false);

  // When manualMatch.round changes, check if matches exist in that round and set time accordingly
  useEffect(() => {
    if (fixtureMode !== 'manual') return;
    const roundMatches = matches.filter(m => m.round === manualMatch.round);
    if (roundMatches.length > 0) {
      // Use the time of the first match in this round
      const roundTime = roundMatches[0].time || '';
      setManualMatch(prev => ({ ...prev, time: roundTime }));
      setManualTimeLocked(true);
    } else {
      setManualTimeLocked(false);
      setManualMatch(prev => ({ ...prev, time: '' }));
    }
    // eslint-disable-next-line
  }, [manualMatch.round, matches, fixtureMode]);

  /* Switch to manual draw, clearing all matches if needed.
     The delete was unchecked, so a refusal still emptied the list on screen
     and switched to manual — leaving somebody building a draw by hand on top
     of one the server still held. If it cannot be cleared, nothing moves. */
  const handleManualDrawClick = async () => {
    if (matches.length > 0) {
      if (!(await showConfirm('Switching to Manual Draw will remove all existing matches. Continue?'))) return;
      try {
        await apiJson(`/api/competitions/${selectedComp.id}/matches`, { method: 'DELETE' });
      } catch (err) {
        showToast(err.message || 'Could not clear the existing draw', 'red');
        return;
      }
      setMatches([]);
    }
    setFixtureMode('manual');
  };

  // Score entry
  const [scoringMatch, setScoringMatch] = useState(null);

  // Edit competition settings
  const [editingCompSettings, setEditingCompSettings] = useState(false);
  const [compEditForm, setCompEditForm] = useState({});
  const [showCompRegs, setShowCompRegs] = useState(false);
  const [settingsQrData, setSettingsQrData] = useState(null);
  const [memberQrData, setMemberQrData] = useState(null);
  const [memberQrLoading, setMemberQrLoading] = useState(false);

  // Draggable settings modal
  const settingsModalRef = useRef(null);
  const settingsDrag = useRef({ active: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const [settingsPos, setSettingsPos] = useState({ x: 0, y: 0 });
  const onSettingsMouseDown = useCallback((e) => {
    if (e.target.closest('button')) return;
    const d = settingsDrag.current;
    d.active = true; d.startX = e.clientX - d.offsetX; d.startY = e.clientY - d.offsetY;
    const onMove = (ev) => { if (!d.active) return; d.offsetX = ev.clientX - d.startX; d.offsetY = ev.clientY - d.startY; setSettingsPos({ x: d.offsetX, y: d.offsetY }); };
    const onUp = () => { d.active = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Quick-add player (for competition registration)
  const [quickName, setQuickName] = useState('');
  const quickRef = useRef(null);
  const [quickAddDetails, setQuickAddDetails] = useState(null);
  const [quickAddSaving, setQuickAddSaving] = useState(false);

  // Dashboard player search
  const [playerSearch, setPlayerSearch] = useState('');

  // Dashboard leaderboard & upcoming matches
  const [dashLeaderboard, setDashLeaderboard] = useState(null);
  const [prevYearLeaderboard, setPrevYearLeaderboard] = useState(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [recentWinners, setRecentWinners] = useState([]);

  // Team management (competition teams)
  const [newTeamName, setNewTeamName] = useState('');
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamPlayerPick, setTeamPlayerPick] = useState('');
  const [quickTeamDetails, setQuickTeamDetails] = useState(null);
  const [quickTeamSaving, setQuickTeamSaving] = useState(false);
  const [quickTeamPlayerId, setQuickTeamPlayerId] = useState('');
  const [quickTeamNewPlayerName, setQuickTeamNewPlayerName] = useState('');
  const [quickTeamAddingPlayer, setQuickTeamAddingPlayer] = useState(false);

  // Club teams
  const [clubTeams, setClubTeams] = useState([]);
  const asClubTeamsArray = (value) => Array.isArray(value) ? value : [];
  const [teamSearch, setTeamSearch] = useState('');
  const [teamProfile, setTeamProfile] = useState(null);
  const [editingTeamProfile, setEditingTeamProfile] = useState(false);
  const [teamProfileForm, setTeamProfileForm] = useState({ name: '', discipline: 'pairs' });

  // Clubs (for linking players/teams to clubs)
  const [clubs, setClubs] = useState([]);

  // Site-wide default theme (super-admin controlled)
  const [siteTheme, setSiteTheme] = useState(null);

  // Club filter — auto-set from user's club for non-super-admins
  const [selectedClubId, setSelectedClubId] = useState('');

  /* The club the header names. The chosen one when there is a choice, and
     otherwise the user's own — a super-admin on "All Clubs" has not picked
     one, and the header should say GreenSheet alone rather than guess. */
  const headerClub = (clubs || []).find(c => String(c.id) === String(selectedClubId))
    || (currentUser && (clubs || []).find(c => String(c.id) === String(currentUser.ClubId)))
    || null;

  /* The year filter runs by the club's playing season — July to June at most
     clubs. A season is named by the year it starts. See utils/season.js. */
  const seasonStartMonth = seasonStartMonthOf(headerClub);
  const compYearFilter = compYearChoice || String(currentSeason(seasonStartMonth));

  /* The club's own site, if the club has filled one in. It is a free-text
     settings field, so "oratiabowlingclub.co.nz" is as likely as a full
     address — and without a scheme the browser reads that as a path on this
     site and lands the member on a blank GreenSheet page. Anything that is
     not plainly http(s) is dropped rather than guessed at: a footer link is
     not the place to find out what someone pasted. */
  /* Applying and renewing are the same decision seen from two sides, so the
     choice is made once here rather than in each screen that offers it.
     Renewal wins when it is due: a member who needs to renew is not helped
     by being invited to apply. */
  const membershipAction = (intent) => () => {
    const clubRef = getMembershipRouteRef(currentUser.ClubId);
    window.history.pushState({}, '', `/membership/${clubRef}${intent === 'renew' ? '/renew' : ''}`);
    setMembershipRoute({ clubRef: decodeURIComponent(clubRef), intent });
  };
  const onRenewMembership = shouldShowRenewButton && renewButtonIsDue ? membershipAction('renew') : null;
  const onApplyMembership = shouldShowApplyMembershipButton ? membershipAction('apply') : null;

  const clubWebsiteHref = (() => {
    const raw = (headerClub && headerClub.website ? String(headerClub.website) : '').trim();
    if (!raw) return null;
    const low = raw.toLowerCase();
    if (low.startsWith('http://') || low.startsWith('https://')) return raw;
    // No scheme. Accept it only if it looks like a bare domain — one word,
    // no spaces, a dot with something after it, and not a path of our own.
    const bare = !/[\s<>"']/.test(raw) && raw.indexOf('/') !== 0 && /^[^.]+\.[^.]{2,}/.test(raw);
    if (bare) return 'https://' + raw;
    return null;
  })();
  useEffect(() => {
    if (currentUser && currentUser.ClubId && currentUser.role !== 'super-admin') {
      setSelectedClubId(String(currentUser.ClubId));
    }
  }, [currentUser]);
  const canCreateCompetition = !(isSuperAdmin && !selectedClubId);

  // Apply club theme colours whenever the current user, clubs list, or club filter changes
  useEffect(() => {
    if (!currentUser) { applyClubTheme(); return; }
    const fallback = siteTheme || {};
    // Super admin viewing a specific club → use that club's theme
    // When no club is explicitly selected (All Clubs), super-admins get the site theme — not their own ClubId fallback
    const clubId = selectedClubId ? Number(selectedClubId) : (isSuperAdmin ? null : currentUser.ClubId);
    const club = clubs.find(c => c.id === clubId);
    if (club) {
      // If club opts into site default (or has never customised), use site theme
      if (club.useDefaultTheme !== false) {
        applyClubTheme(fallback.c60, fallback.c30, fallback.c10);
      } else {
        // Club has its own custom colours; fall back to site theme then hardcoded default
        applyClubTheme(
          club.themeC60 || fallback.c60,
          club.themeC30 || fallback.c30,
          club.themeC10 || fallback.c10
        );
      }
    } else {
      // "All Clubs" → apply site theme (or defaults)
      applyClubTheme(fallback.c60, fallback.c30, fallback.c10);
    }
  }, [currentUser, clubs, selectedClubId, siteTheme]); // eslint-disable-line

  // Toggle body class when a club logo is visible as dashboard background
  useEffect(() => {
    const dashClubId = selectedClubId ? Number(selectedClubId) : (isSuperAdmin ? null : currentUser && currentUser.ClubId);
    const dashClub = clubs.find(c => c.id === dashClubId);
    if (view === 'dashboard' && dashClub && dashClub.logo) {
      document.body.classList.add('has-logo-bg');
    } else {
      document.body.classList.remove('has-logo-bg');
    }
    return () => document.body.classList.remove('has-logo-bg');
  }, [view, selectedClubId, clubs, currentUser, isSuperAdmin]);

  // Pending counts for admin header badge
  const [pendingRegCount, setPendingRegCount] = useState(0);
  const [pendingMembershipCount, setPendingMembershipCount] = useState(0);
  const [pendingScoreCount, setPendingScoreCount] = useState(0);

  const getMembershipRouteRef = useCallback((clubId) => {
    if (!clubId) return '';
    const match = clubs.find(c => String(c.id) === String(clubId));
    const refSource = (match && match.name) ? String(match.name).trim() : String(clubId);
    return encodeURIComponent(refSource);
  }, [clubs]);
  const [showPendingRegsModal, setShowPendingRegsModal] = useState(false);
  /* "Which competitions am I in", as the home page reads it. Called on load
     and again the moment a registration goes in: without the second call the
     competition just entered sat under "Open for entry" — offering to enter
     it again — until the page was reloaded. */
  const refreshMyRegistrations = useCallback(() => {
    if (!authToken) return;
    fetch(`${API}/api/registrations/my/all`)
      .then(r => (r.ok ? r.json() : {}))
      .then(d => setMyRegistrations(d && typeof d === 'object' ? d : {}))
      .catch(() => {});
    /* Fetched with it, because the two together are what "am I in this"
       means. Registering does not create a player-list entry until an admin
       approves it, and being entered by hand never creates a registration. */
    fetch(`${API}/api/competitions/my/ids`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setMyCompetitionIds(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [authToken]);

  const refreshPendingCounts = useCallback(() => {
    if (!authToken) return;

    fetch(`${API}/api/registrations/pending-count`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setPendingRegCount(d.count || 0))
      .catch(() => setPendingRegCount(0));

    fetch(`${API}/api/users/pending-membership-count`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setPendingMembershipCount(d.count || 0))
      .catch(() => setPendingMembershipCount(0));

    fetch(`${API}/api/matches/pending-score-count`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setPendingScoreCount(d.count || 0))
      .catch(() => setPendingScoreCount(0));
  }, [authToken]);

  useEffect(() => {
    if (!isLoggedIn || !currentUser) {
      setMemberQrData(null);
      return;
    }

    let cancelled = false;
    setMemberQrLoading(true);
    fetch(`${API}/api/users/my/membership-qr?baseUrl=${encodeURIComponent(PUBLIC_APP_ORIGIN)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!cancelled) setMemberQrData(data || null);
      })
      .catch(() => {
        if (!cancelled) setMemberQrData(null);
      })
      .finally(() => {
        if (!cancelled) setMemberQrLoading(false);
      });

    return () => { cancelled = true; };
  }, [isLoggedIn, currentUser && currentUser.id]);

  // Toast notifications
  const [toast, setToast] = useState(null);
  /* Stable, so the useCallbacks that report failures through it can depend on
     it honestly rather than leaving it out of their dependency list. It only
     ever touches setToast, which React guarantees does not change. */
  const showToast = useCallback((msg, type = 'green') => {
    const safeMsg = (typeof msg === 'string' && msg.trim()) ? msg : 'Done';
    setToast({ msg: safeMsg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Custom confirm dialog (replaces window.confirm)
  const [confirmState, setConfirmState] = useState(null);
  const showConfirm = (message) => {
    return new Promise((resolve) => {
      setConfirmState({ message, resolve });
    });
  };
  const handleConfirmOk = () => {
    if (confirmState) confirmState.resolve(true);
    setConfirmState(null);
  };
  const handleConfirmCancel = () => {
    if (confirmState) confirmState.resolve(false);
    setConfirmState(null);
  };

  // Custom prompt dialog (replaces window.prompt)
  const [promptState, setPromptState] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const showPrompt = (message, defaultValue = '') => {
    return new Promise((resolve) => {
      setPromptValue(defaultValue);
      setPromptState({ message, resolve });
    });
  };
  const handlePromptOk = () => {
    if (promptState) promptState.resolve(promptValue);
    setPromptState(null);
    setPromptValue('');
  };
  const handlePromptCancel = () => {
    if (promptState) promptState.resolve(null);
    setPromptState(null);
    setPromptValue('');
  };

  // Helper to build query string for club filtering
  const clubQuery = selectedClubId ? `?clubId=${selectedClubId}` : '';
  const yearQuery = compYearFilter !== 'all' ? `year=${compYearFilter}` : '';
  const yearQuerySep = yearQuery ? (clubQuery ? `&${yearQuery}` : `?${yearQuery}`) : '';

  const refreshClubLeaderboard = useCallback(() => {
    if (!authToken) return;
    fetch(`${API}/api/users/leaderboard/all${clubQuery}${yearQuerySep}`)
      .then(r => r.json())
      .then(lb => setDashLeaderboard(lb))
      .catch(() => setDashLeaderboard(null));
  }, [authToken, clubQuery, yearQuerySep]);

  /* --- Data loading --- */
  const loadDashboardExtras = useCallback((competitions) => {
    // Recent winners from completed competitions
    fetch(`${API}/api/competitions/recent-winners/all${clubQuery}`).then(r => r.json())
      .then(w => setRecentWinners(Array.isArray(w) ? w : (Array.isArray(w && w.winners) ? w.winners : [])))
      .catch(() => setRecentWinners([]));

    // Gather dashboard matches from active/finished competitions
    const dashboardComps = competitions.filter(c => c.status === 'in-progress' || c.status === 'completed');
    Promise.all(dashboardComps.map(c =>
      fetch(`${API}/api/competitions/${c.id}/matches`).then(r => r.json())
        .then(ms => {
          const matches = Array.isArray(ms) ? ms : [];
          const uniqueRinks = new Set(matches.map(m => m.rink).filter(Boolean));
          const uniqueGreens = new Set(matches.map(m => m.green).filter(Boolean));
          const compShowRink = uniqueRinks.size > 1;
          const compShowGreen = uniqueGreens.size > 1;
          return matches.map(m => ({ ...m, compName: c.name, compId: c.id, compStartDate: c.startDate, compRoundStartDates: c.roundStartDates || {}, compTournamentType: c.tournamentType, compShowRink, compShowGreen }));
        })
        .catch(() => [])
    )).then(arrays => {
      const parseScheduledDateTime = (m) => {
        // Use the match's own date first, then round date, then comp start date.
        const matchDate = m.date || (m.compRoundStartDates && m.compRoundStartDates[m.round]) || m.compStartDate;
        if (matchDate && m.time) {
          let dateStr = (matchDate + '').split('T')[0];
          const dt = new Date(`${dateStr}T${m.time.padStart(5, '0')}:00`);
          return Number.isNaN(dt.getTime()) ? null : dt;
        }
        if (matchDate) {
          const d = new Date(matchDate);
          return Number.isNaN(d.getTime()) ? null : d;
        }
        return null;
      };

      const parseRecentDateTime = (m) => {
        if (m.updatedAt) {
          const d = new Date(m.updatedAt);
          if (!Number.isNaN(d.getTime())) return d;
        }
        return parseScheduledDateTime(m);
      };

      const all = arrays.flat();
      const upcoming = all.filter(m => {
        if (m.completed || m.result === 'bye') return false;
        const isDoubleElim = (m.compTournamentType || 'round-robin') === 'double-elimination';
        if (!isDoubleElim) return true;
        return !!(m.homeTeamId || m.awayTeamId);
      }).sort((a, b) => {
        const da = parseScheduledDateTime(a);
        const db = parseScheduledDateTime(b);
        if (da && db) {
          if (da.getTime() !== db.getTime()) return da - db;
        } else if (da) {
          return -1;
        } else if (db) {
          return 1;
        }
        const ra = a.rink || 9999;
        const rb = b.rink || 9999;
        return ra - rb;
      });

      const recent = all.filter(m => m.completed && m.result && m.result !== 'bye' && m.result !== 'not-played').sort((a, b) => {
        const da = parseRecentDateTime(a);
        const db = parseRecentDateTime(b);
        if (da && db) {
          if (da.getTime() !== db.getTime()) return db - da;
        } else if (da) {
          return -1;
        } else if (db) {
          return 1;
        }
        return (b.id || 0) - (a.id || 0);
      });

      // Resolve each match's scheduled time once, here, so the dashboard can
      // pick out "today" without re-deriving the date fallback chain.
      setUpcomingMatches(upcoming.slice(0, 10).map(m => {
        const dt = parseScheduledDateTime(m);
        return { ...m, scheduledAt: dt ? dt.toISOString() : null };
      }));
      setRecentMatches(recent.slice(0, 10));
    });
  }, [clubQuery]);

  // Leaderboard: single source of truth, re-fetches on year or club change
  useEffect(() => {
    if (!authToken) return;
    fetch(`${API}/api/users/leaderboard/all${clubQuery}${yearQuerySep}`).then(r => r.json())
      .then(lb => setDashLeaderboard(lb))
      .catch(() => setDashLeaderboard(null));
  }, [yearQuerySep, clubQuery, authToken]); // eslint-disable-line

  // Previous year leaderboard (for year-over-year analytics cards)
  useEffect(() => {
    if (!authToken) return;
    const yr = parseInt(compYearFilter, 10);
    if (Number.isNaN(yr)) {
      setPrevYearLeaderboard(null);
      return;
    }
    const prevYear = yr - 1;
    const prevYearQuerySep = clubQuery ? `&year=${prevYear}` : `?year=${prevYear}`;
    fetch(`${API}/api/users/leaderboard/all${clubQuery}${prevYearQuerySep}`).then(r => r.json())
      .then(lb => setPrevYearLeaderboard(lb))
      .catch(() => setPrevYearLeaderboard(null));
  }, [compYearFilter, clubQuery, authToken]); // eslint-disable-line

  useEffect(() => {
    if (!authToken) return; // Don't fetch data when not logged in
    fetch(`${API}/api/users${clubQuery}`).then(r => r.json()).then(setUsers).catch(() => {});
    fetch(`${API}/api/competitions${clubQuery}`).then(r => r.json()).then(c => { const arr = Array.isArray(c) ? c : []; setComps(arr); loadDashboardExtras(arr); }).catch(() => {});
    fetch(`${API}/api/clubteams${clubQuery}`)
      .then(r => r.json())
      .then(data => setClubTeams(asClubTeamsArray(data)))
      .catch(() => setClubTeams([]));
    fetch(`${API}/api/templates${clubQuery}`).then(r => r.json()).then(setCustomTemplates).catch(() => {});
    // Fetch site + club competition defaults and build rules catalogs
    const clubId = selectedClubId || (currentUser && currentUser.ClubId);
    fetch(`${API}/api/auth/site-comp-defaults`).then(r => r.ok ? r.json() : null).then(siteDefaults => {
      const safeSite = siteDefaults || null;
      setSiteCompDefaults(safeSite);
      const generic = normalizeRulesList((safeSite && safeSite.rulesLibraryGeneric) || DEFAULT_GENERIC_RULES, 'site');

      if (clubId) {
        fetch(`${API}/api/auth/clubs/${clubId}/comp-defaults`).then(r => r.ok ? r.json() : null).then(clubDefaults => {
          const safeClub = clubDefaults || null;
          setClubCompDefaults(safeClub);
          setRulesLibrary({
            generic,
            custom: normalizeRulesList((safeClub && safeClub.rulesLibraryClubCustom) || [], 'club'),
          });
        }).catch(() => {
          setClubCompDefaults(null);
          setRulesLibrary({ generic, custom: [] });
        });
      } else {
        setClubCompDefaults(isSuperAdmin ? safeSite : null);
        setRulesLibrary({ generic, custom: [] });
      }
    }).catch(() => {
      setSiteCompDefaults(null);
      setRulesLibrary({ generic: DEFAULT_GENERIC_RULES, custom: [] });
      if (!clubId) setClubCompDefaults(null);
    });
    /* The full list is admin-only. A member gets their own club instead, so
       the header can name it, the footer can link to it and the theme can be
       applied — none of which worked for anyone who was not an admin. */
    fetch(`${API}/api/auth/clubs`)
      .then(r => (r.ok ? r.json() : null))
      .then(list => {
        if (Array.isArray(list) && list.length) { setClubs(list); return null; }
        return fetch(`${API}/api/auth/my-club`)
          .then(r => (r.ok ? r.json() : null))
          .then(mine => setClubs(mine ? [mine] : []));
      })
      .catch(() => {});
    fetch(`${API}/api/auth/site-theme`).then(r => r.ok ? r.json() : null).then(setSiteTheme).catch(() => {});
    /* Everyone signed in, admins included. An admin is a player too, and
       leaving them out meant the home page never knew what they had entered
       — so a competition they were already in stayed under "Open for entry"
       until the draw was made. */
    if (authToken) refreshMyRegistrations();
    refreshPendingCounts();
  }, [selectedClubId, authToken]); // eslint-disable-line

  // When super-admin switches clubs, reset comp form to the new club's effective defaults
  useEffect(() => {
    if (view === 'newComp' && selectedPreset) {
      setCompForm(f => ({ ...(clubCompDefaults ? mergeCompDefaults(DEFAULT_FORM, clubCompDefaults) : mergeCompDefaults()), name: f.name, startDate: f.startDate, endDate: f.endDate }));
    }
  }, [clubCompDefaults]); // eslint-disable-line

  const goBack = () => {
    // Use browser history so in-app back and browser back stay in sync
    window.history.back();
  };

  /* The five things a competition screen is made of.
   *
     openComp and refreshComp each kept their own copy of this list, and both
     copies read `.then(r => r.json()).then(setTeams)` — which on a refusal
     put { error: 'Not your club' } where the screen expects an array. The
     next render called teams.map and took the whole app down to a white
     page. A club admin opening a competition belonging to another club was
     enough to do it.
   *
     Now: fetched together rather than one after another, a list is
     guaranteed to be a list or the load fails loudly, and a part that will
     not load leaves its section empty and says so instead of poisoning it.
     allSettled rather than all, because four sections loading is better
     than none. */
  const loadCompDetails = useCallback(async (compId) => {
    const results = await Promise.allSettled([
      apiJson(`/api/competitions/${compId}`),
      apiList(`/api/competitions/${compId}/players`),
      apiList(`/api/competitions/${compId}/teams`),
      apiList(`/api/competitions/${compId}/matches`),
      apiJson(`/api/competitions/${compId}/leaderboard`),
    ]);
    const [comp, players, compTeams, compMatches, board] = results;

    /* The competition itself is the exception: it was already set from the
       row that was clicked, and that is better than nothing. */
    if (comp.status === 'fulfilled') setSelectedComp(comp.value);
    setCompPlayers(players.status === 'fulfilled' ? players.value : []);
    setTeams(compTeams.status === 'fulfilled' ? compTeams.value : []);
    setMatches(compMatches.status === 'fulfilled' ? compMatches.value : []);
    setLeaderboard(board.status === 'fulfilled' ? board.value : null);

    const failure = results.find(r => r.status === 'rejected');
    if (failure) {
      showToast(
        (failure.reason && failure.reason.message) || 'Some of this competition could not be loaded',
        'red',
      );
    }
  }, [showToast]);

  const openComp = useCallback((comp) => {
    setViewTracked('competition');
    setSelectedComp(comp);
    setActiveStep('home');
    setFixtureMode('auto');
    window.history.replaceState({ view: 'competition' }, '', `#comp/${comp.id}`);
    loadCompDetails(comp.id);
  }, [loadCompDetails]);

  // Restore view from URL hash on page load
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    if (hash === 'competition-rules') { setViewTracked('competitionRules'); }
    else if (hash === 'players') { setViewTracked('clubPlayers'); }
    else if (hash === 'teams') { setViewTracked('clubTeams'); }
    else if (hash === 'admin') { setViewTracked('adminUsers'); }
    else if (hash === 'help') { setViewTracked('help'); }
    else if (hash === 'stats') { setViewTracked('clubStats'); }
    else if (hash === 'member-id') { setViewTracked('memberId'); }
    else if (hash.startsWith('comp/')) {
      const compId = parseInt(hash.split('/')[1]);
      if (compId) pendingHashRestore.current = compId;
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    document.title = view === 'memberId' ? 'Membership ID' : 'GreenSheet';
  }, [view]);

  // Deferred: open competition once comps list is loaded
  useEffect(() => {
    if (pendingHashRestore.current && comps.length > 0) {
      const compId = pendingHashRestore.current;
      pendingHashRestore.current = null;
      const comp = comps.find(c => c.id === compId);
      if (comp) openComp(comp);
    }
  }, [comps, openComp]);

  const refreshComp = useCallback(() => {
    if (!selectedComp) return;
    loadCompDetails(selectedComp.id);
    // Approving a team entry can mint a club team, so pull the club list
    // again or the new team's players keep their "inactive" tags.
    fetch(`${API}/api/clubteams${clubQuery}`)
      .then(r => r.json())
      .then(data => setClubTeams(asClubTeamsArray(data)))
      .catch(() => {});
  }, [selectedComp, clubQuery, loadCompDetails]);

  useEffect(() => {
    if (!isAdmin || !selectedComp || activeStep !== 'players') return;
    const clubIdForTemplates = selectedComp.ClubId || selectedClubId || (currentUser && currentUser.ClubId);
    const query = clubIdForTemplates ? `?clubId=${clubIdForTemplates}` : '';
    fetch(`${API}/api/emails/templates${query}`)
      .then(r => r.ok ? r.json() : [])
      .then(ts => setCompetitionEmailTemplates(Array.isArray(ts) ? ts : []))
      .catch(() => setCompetitionEmailTemplates([]));
  }, [isAdmin, selectedComp, activeStep, selectedClubId, currentUser]);

  useEffect(() => {
    const teamMemberIds = new Set(teams.flatMap(t => (t.Users || []).map(u => u.id)));
    const pool = (activeStep === 'teams' && selectedComp && selectedComp.format !== 'singles')
      ? compPlayers.filter(p => teamMemberIds.has(p.id))
      : compPlayers;
    const eligibleIds = new Set(pool.filter(p => !!p.email).map(p => p.id));
    setSelectedCompPlayerIds(prev => prev.filter(id => eligibleIds.has(id)));
  }, [compPlayers, teams, activeStep, selectedComp]);

  const sendCompetitionEmail = async () => {
    const teamMemberIds = new Set(teams.flatMap(t => (t.Users || []).map(u => u.id)));
    const emailPool = (activeStep === 'teams' && selectedComp && selectedComp.format !== 'singles')
      ? compPlayers.filter(p => teamMemberIds.has(p.id))
      : compPlayers;
    const selectedWithEmail = emailPool.filter(p => selectedCompPlayerIds.includes(p.id) && !!p.email);
    if (selectedWithEmail.length === 0) {
      showToast('Select at least one player with an email address', 'orange');
      return;
    }

    const registrationTemplate = competitionEmailTemplates.find(t => t.type === 'registration');
    const isQuickGeneral = compEmailMode === 'general' && compGeneralSendMode === 'quick';
    const templateId = compEmailMode === 'entry-confirmation'
      ? (registrationTemplate ? registrationTemplate.id : null)
      : (compGeneralSendMode === 'template' && compGeneralTemplateId ? parseInt(compGeneralTemplateId) : null);

    if (compEmailMode === 'entry-confirmation' && !templateId) {
      showToast('Competition entry confirmation template not found', 'orange');
      return;
    }

    if (compEmailMode === 'general' && compGeneralSendMode === 'template' && !templateId) {
      showToast('Select a general email template', 'orange');
      return;
    }

    if (isQuickGeneral && (!compQuickEmailSubject.trim() || !compQuickEmailBody.trim())) {
      showToast('Enter a quick email subject and message', 'orange');
      return;
    }

    setSendingCompEmail(true);
    try {
      const entryFeeValue = Number(selectedComp && selectedComp.entryFee);
      const hasEntryFee = Number.isFinite(entryFeeValue) && entryFeeValue > 0;
      const payload = {
        recipientType: 'specific-users',
        recipientUserIds: selectedWithEmail.map(p => p.id),
        sendNow: true,
        competitionName: selectedComp && selectedComp.name ? selectedComp.name : '',
        competitionEntryFee: hasEntryFee ? entryFeeValue.toFixed(2) : '0.00',
        competitionEntryFeeDisplay: hasEntryFee ? `$${entryFeeValue.toFixed(2)}` : 'Free',
        clubId: selectedComp && selectedComp.ClubId ? selectedComp.ClubId : undefined,
      };

      if (isQuickGeneral) {
        payload.customSubject = compQuickEmailSubject.trim();
        payload.customBody = compQuickEmailBody;
      } else {
        payload.templateId = templateId;
        payload.allowRegistrationTemplate = compEmailMode === 'entry-confirmation';
      }

      const r = await fetch(`${API}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to send emails');
      showToast(`Email queued for ${selectedWithEmail.length} player${selectedWithEmail.length !== 1 ? 's' : ''}`);
      setSelectedCompPlayerIds([]);
      if (isQuickGeneral) {
        setCompQuickEmailSubject('');
        setCompQuickEmailBody('');
      }
    } catch (err) {
      showToast(err.message || 'Failed to send emails', 'red');
    } finally {
      setSendingCompEmail(false);
    }
  };

  // Auto-refresh competition data every 15 seconds when viewing a competition
  useEffect(() => {
    if (view !== 'competition' || !selectedComp) return;
    const interval = setInterval(() => {
      refreshComp();
    }, 15000);
    return () => clearInterval(interval);
  }, [view, selectedComp, refreshComp]);

  /* --- Player Profile --- */
  /* The screen used to switch to the profile view before the profile had
     loaded, and a refusal — a 403 on somebody from another club, say — was
     written into playerProfile as { error: ... }. That left an entirely
     blank profile page with no name, no message and no way to tell it apart
     from a real player with nothing filled in. Now a failure says so and
     leaves the screen where it was. */
  const openProfile = useCallback(async (userId) => {
    setEditingProfile(false);
    setResetPwOpen(false);
    setResetPwValue('');
    try {
      const p = await apiJson(`/api/users/${userId}/profile`);
      setPlayerProfile(p);
      setProfileForm({
        name: p.name || '',
        nickname: p.nickname || '',
        bowlingStartYear: p.bowlingStartYear || '',
        email: p.email || '',
        phone: p.phone || '',
        membershipType: p.membershipType || '',
        playerType: p.playerType || '',
        ageGroup: p.ageGroup || '',
        ClubId: p.ClubId || '',
        membershipExtraData: (p.membershipExtraData && typeof p.membershipExtraData === 'object' && !Array.isArray(p.membershipExtraData)) ? p.membershipExtraData : {},
        /* Only an admin is sent the record, and only an admin's save sends
           it back — a form without it leaves the membership alone. */
        ...(p.membershipRecord ? {
          membershipNumber: p.membershipNumber || '',
          membershipStanding: String(p.membershipApprovalStatus || '').toLowerCase() === 'pending'
            ? 'pending'
            : (p.membershipRecord.membershipApprovedAt ? 'approved' : 'none'),
          ...Object.fromEntries(Object.entries(p.membershipRecord).map(([k, v]) => [k, toDateInputValue(v)])),
        } : {}),
      });
      if (viewRef.current !== 'clubPlayers') setViewTracked('playerProfile');
    } catch (err) {
      showToast(err.message || 'Could not load that profile', 'red');
    }
  }, [showToast]);

  const openNewProfile = () => {
    setPlayerProfile({ id: null, name: '', nickname: '', bowlingStartYear: '', email: '', phone: '', membershipType: '', playerType: '', membershipExtraData: {}, createdAt: new Date().toISOString(), competitions: [], competitionStats: [], totals: { played: 0, wins: 0, losses: 0, draws: 0, pointsFor: 0, pointsAgainst: 0 } });
    setProfileForm({ name: '', nickname: '', bowlingStartYear: '', email: '', phone: '', membershipType: '', playerType: '', ageGroup: '', ClubId: currentUser ? currentUser.ClubId || '' : '', membershipExtraData: {} });
    setEditingProfile(true);
    if (viewRef.current !== 'clubPlayers') setViewTracked('playerProfile');
  };

  const saveProfile = () => {
    if (!playerProfile) return;
    const selectedClubId = profileForm.ClubId || (playerProfile ? playerProfile.ClubId : null);
    const selectedClub = clubs.find(c => String(c.id) === String(selectedClubId));
    const selectedConfig = normalizeMembershipFormConfig(selectedClub && selectedClub.membershipFormConfig);
    const selectedPlatformFields = (selectedConfig.fields || [])
      .filter(field => field && field.enabled && field.storage === 'platform');
    const membershipExtraSource = (profileForm.membershipExtraData && typeof profileForm.membershipExtraData === 'object' && !Array.isArray(profileForm.membershipExtraData))
      ? profileForm.membershipExtraData
      : {};
    const membershipExtraData = { ...membershipExtraSource };
    selectedPlatformFields
      .filter(field => field.isCustom)
      .forEach((field) => {
        const raw = membershipExtraSource[field.key];
        if (field.type === 'boolean') {
          membershipExtraData[field.key] = !!raw;
          return;
        }
        if (field.type === 'number') {
          const txt = String(raw || '').trim();
          if (!txt) {
            delete membershipExtraData[field.key];
            return;
          }
          const n = Number(txt);
          if (Number.isFinite(n)) membershipExtraData[field.key] = n;
          else delete membershipExtraData[field.key];
          return;
        }
        const txt = String(raw || '').trim();
        if (!txt) delete membershipExtraData[field.key];
        else membershipExtraData[field.key] = txt;
      });

    const payload = {
      ...profileForm,
      bowlingStartYear: profileForm.bowlingStartYear === '' || profileForm.bowlingStartYear === null || profileForm.bowlingStartYear === undefined
        ? null
        : parseInt(profileForm.bowlingStartYear, 10),
      membershipExtraData,
    };
    if (playerProfile.id) {
      // Update existing
      fetch(`${API}/api/users/${playerProfile.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || 'Could not save the profile.');
        return body;
      })
        .then(updated => {
          setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
          setEditingProfile(false);
          showToast('Profile saved!');
          // Re-fetch full profile to get updated Club info
          openProfile(playerProfile.id);
          // Refresh competition data so the new name shows in matches, leaderboard, teams etc.
          if (selectedComp) refreshComp();
        })
        .catch(err => showToast(err.message || 'Could not save the profile.', 'red'));
    } else {
      // Create new player
      if (!profileForm.name.trim()) { showToast('Name is required', 'red'); return; }
      /* The server's reason, not ours. `throw new Error()` used to discard
         it and the catch showed a flat "Failed to create player", so an
         admin was told something had gone wrong and never which box to go
         and look at. */
      fetch(`${API}/api/users`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      }).then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || 'Could not create the player.');
        return body;
      })
        .then(created => {
          setUsers(prev => [...prev, created]);
          showToast(`${created.name} created!`);
          // Now open the real profile
          openProfile(created.id);
        })
        .catch(err => showToast(err.message || 'Could not create the player.', 'red'));
    }
  };

  const confirmMembershipForProfile = async () => {
    if (!playerProfile || !playerProfile.id || !isAdmin) return;
    const label = playerProfile.membershipPendingIntent === 'renew' ? 'renewal' : 'membership';
    if (!(await showConfirm(`Confirm this ${label} for ${playerProfile.name}?`))) return;
    try {
      const r = await fetch(`${API}/api/users/${playerProfile.id}/confirm-membership`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Failed to confirm membership');
      showToast(data.message || 'Membership confirmed', 'green');
      openProfile(playerProfile.id);
    } catch (err) {
      showToast(err.message || 'Failed to confirm membership', 'red');
    }
  };

  const resetPlayerPassword = () => {
    if (!playerProfile || !playerProfile.id || !resetPwValue) return;
    if (resetPwValue.length < 8) { showToast('Password must be at least 8 characters', 'red'); return; }
    fetch(`${API}/api/auth/users/${playerProfile.id}/reset-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
      body: JSON.stringify({ newPassword: resetPwValue }),
    }).then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Failed'); }); return r.json(); })
      .then(d => {
        showToast(d.message || 'Password reset!', 'green');
        setResetPwOpen(false); setResetPwValue('');
      })
      .catch(e => showToast(e.message || 'Failed to reset password', 'red'));
  };

  const deletePlayer = async () => {
    if (!playerProfile) return;
    if (!(await showConfirm(`Are you sure you want to permanently delete ${playerProfile.name}? This will remove them from all competitions and teams.`))) return;
    if (!(await showConfirm(`This cannot be undone. Click OK to confirm deletion of ${playerProfile.name}.`))) return;
    fetch(`${API}/api/users/${playerProfile.id}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setUsers(prev => prev.filter(u => u.id !== playerProfile.id));
        setPlayerProfile(null);
        if (viewRef.current !== 'clubPlayers') setView('home');
        showToast('Player deleted');
      })
      .catch(() => showToast('Failed to delete', 'red'));
  };

  /* --- Club Team functions --- */
  const openTeamProfile = useCallback((teamId) => {
    if (viewRef.current !== 'clubTeams') setViewTracked('teamProfile');
    setEditingTeamProfile(false);
    fetch(`${API}/api/clubteams/${teamId}/profile`).then(r => r.json()).then(p => {
      setTeamProfile(p);
      setTeamProfileForm({ name: p.name || '', discipline: p.discipline || 'pairs', ClubId: p.ClubId || '' });
    }).catch(() => showToast('Failed to load team profile', 'red'));
  }, []);

  /* --- Competition team → profile helper --- */
  const handleCompTeamClick = useCallback((compTeamId, e) => {
    if (e) e.stopPropagation();
    const team = teams.find(t => t.id === compTeamId);
    if (!team) return;
    // Singles: one player per team → open player profile
    if (team.Users && team.Users.length === 1) {
      openProfile(team.Users[0].id);
    } else if (team.ClubTeamId) {
      openTeamProfile(team.ClubTeamId);
    } else if (team.Users && team.Users.length > 0) {
      // Multi-player team without a club team link — open first player
      openProfile(team.Users[0].id);
    }
  }, [teams, openProfile, openTeamProfile]);

  const openNewTeamProfile = () => {
    setTeamProfile({ id: null, name: '', discipline: 'pairs', maxActive: 2, createdAt: new Date().toISOString(), players: [], competitions: [], competitionStats: [], totals: { played: 0, wins: 0, losses: 0, draws: 0, pointsFor: 0, pointsAgainst: 0 } });
    setTeamProfileForm({ name: '', discipline: 'pairs', ClubId: currentUser ? currentUser.ClubId || '' : '' });
    setEditingTeamProfile(true);
    if (viewRef.current !== 'clubTeams') setViewTracked('teamProfile');
  };

  const saveTeamProfile = () => {
    if (!teamProfile) return;
    if (teamProfile.id) {
      fetch(`${API}/api/clubteams/${teamProfile.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: teamProfileForm.name, discipline: teamProfileForm.discipline, ClubId: teamProfileForm.ClubId || null }),
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(updated => {
          setClubTeams(prev => asClubTeamsArray(prev).map(t => t.id === updated.id ? { ...t, name: updated.name, discipline: updated.discipline } : t));
          setEditingTeamProfile(false);
          showToast('Team saved!');
          // Re-fetch full profile to get updated Club info
          openTeamProfile(teamProfile.id);
          // Refresh competition data so the renamed team shows in matches, leaderboard etc.
          if (selectedComp) refreshComp();
        })
        .catch(() => showToast('Failed to save', 'red'));
    } else {
      if (!teamProfileForm.name.trim()) { showToast('Team name is required', 'red'); return; }
      fetch(`${API}/api/clubteams`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: teamProfileForm.name, discipline: teamProfileForm.discipline, ClubId: teamProfileForm.ClubId || undefined }),
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(created => {
          setClubTeams(prev => [...asClubTeamsArray(prev), created]);
          showToast(`${created.name} created!`);
          openTeamProfile(created.id);
        })
        .catch(() => showToast('Failed to create team', 'red'));
    }
  };

  /* Resolves true or false, so a caller that has just created the player —
     the team sheet's "Add guest" — knows whether to say it worked. */
  const addPlayerToClubTeam = (userId) => {
    if (!teamProfile || !teamProfile.id) return Promise.resolve(false);
    return fetch(`${API}/api/clubteams/${teamProfile.id}/players`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    }).then(async r => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Failed to add player');
      return body;
    })
      .then(() => { openTeamProfile(teamProfile.id); return true; })
      .catch(err => { showToast(err.message || 'Failed to add player', 'red'); return false; });
  };

  const removePlayerFromClubTeam = (userId) => {
    if (!teamProfile || !teamProfile.id) return;
    fetch(`${API}/api/clubteams/${teamProfile.id}/players/${userId}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => openTeamProfile(teamProfile.id))
      .catch(() => showToast('Failed to remove player', 'red'));
  };

  const togglePlayerActive = (userId) => {
    if (!teamProfile || !teamProfile.id) return;
    fetch(`${API}/api/clubteams/${teamProfile.id}/players/${userId}/active`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
    }).then(r => {
      if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Failed'); });
      return r.json();
    })
      .then(() => {
        openTeamProfile(teamProfile.id);
        // Refresh clubTeams so active status is up-to-date on competition pages
        fetch(`${API}/api/clubteams`)
          .then(r => r.json())
          .then(data => setClubTeams(asClubTeamsArray(data)))
          .catch(() => setClubTeams([]));
      })
      .catch(e => showToast(e.message || 'Failed to toggle active', 'red'));
  };

  const deleteClubTeam = async () => {
    if (!teamProfile) return;
    if (!(await showConfirm(`Delete team "${teamProfile.name}"? This cannot be undone.`))) return;
    fetch(`${API}/api/clubteams/${teamProfile.id}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setClubTeams(prev => asClubTeamsArray(prev).filter(t => t.id !== teamProfile.id));
        setTeamProfile(null);
        if (viewRef.current !== 'clubTeams') setView('home');
        showToast('Team deleted');
      })
      .catch(() => showToast('Failed to delete', 'red'));
  };

  /* --- Competition CRUD --- */
  // Effective defaults = site defaults merged with club overrides
  const getEffectiveDefaults = () => {
    const withSite = mergeCompDefaults(DEFAULT_FORM, siteCompDefaults || {});
    return mergeCompDefaults(withSite, clubCompDefaults || {});
  };

  /* Start a competition from somewhere that already knows the date — the
     club calendar, so far. The setup screen is the only place a competition
     can be created, and it should stay that way: a comp needs a format, a
     draw and a rules set, none of which fit in a day cell. All this does is
     open that screen with the date already filled in. */
  const startNewCompOn = (dateISO) => {
    setSelectedPreset(null);
    setCompForm({ ...getEffectiveDefaults(), startDate: dateISO || '', endDate: dateISO || '' });
    setShowCustomSettings(true);
    // Not touched by a person, so typing a start date still drags the end
    // date along with it, exactly as it does from the dashboard.
    endDateTouched.current = false;
    setViewTracked('newComp');
  };

  const saveClubDefaults = () => {
    const clubId = selectedClubId || (currentUser && currentUser.ClubId);
    const { name, startDate, endDate, appliedRules, ...settings } = compForm;
    // Super-admin on "All Clubs" saves site-wide defaults
    if (isSuperAdmin && !selectedClubId) {
      fetch(`${API}/api/auth/site-comp-defaults`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaults: settings }),
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(d => { setClubCompDefaults(d); showToast('Site defaults saved!'); })
        .catch(() => showToast('Failed to save site defaults', 'red'));
      return;
    }
    if (!clubId) return;
    fetch(`${API}/api/auth/clubs/${clubId}/comp-defaults`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ defaults: settings }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setClubCompDefaults(d); showToast('Club defaults saved!'); })
      .catch(() => showToast('Failed to save defaults', 'red'));
  };

  const restoreSiteDefaults = () => {
    const clubId = selectedClubId || (currentUser && currentUser.ClubId);
    // Super-admin on "All Clubs" clears site-wide defaults
    if (isSuperAdmin && !selectedClubId) {
      fetch(`${API}/api/auth/site-comp-defaults`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaults: null }),
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => {
          setClubCompDefaults(null);
          setCompForm(f => ({ ...mergeCompDefaults(), name: f.name, startDate: f.startDate, endDate: f.endDate }));
          showToast('Site defaults restored');
        })
        .catch(() => showToast('Failed to restore site defaults', 'red'));
      return;
    }
    if (!clubId) return;
    fetch(`${API}/api/auth/clubs/${clubId}/comp-defaults`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ defaults: null }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setClubCompDefaults(null);
        setCompForm(f => ({ ...mergeCompDefaults(), name: f.name, startDate: f.startDate, endDate: f.endDate }));
        showToast('Restored site defaults');
      })
      .catch(() => showToast('Failed to restore defaults', 'red'));
  };

  const normalizeCompNumericFields = (raw) => {
    const payload = { ...raw };
    const intOr = (v, d) => (v === '' || v === null || v === undefined || Number.isNaN(parseInt(v, 10)) ? d : parseInt(v, 10));
    const floatOr = (v, d) => (v === '' || v === null || v === undefined || Number.isNaN(parseFloat(v)) ? d : parseFloat(v));
    const rulesBank = payload.rulesBank && typeof payload.rulesBank === 'object' ? payload.rulesBank : {};
    const appliedRules = normalizeRulesList(payload.appliedRules || [], 'club');

    payload.numberOfEnds = intOr(payload.numberOfEnds, 18);
    payload.targetScore = intOr(payload.targetScore, 21);
    payload.timeLimit = intOr(payload.timeLimit, 75);
    payload.endsPerSet = intOr(payload.endsPerSet, 9);
    payload.numberOfSets = intOr(payload.numberOfSets, 3);
    payload.numberOfRounds = intOr(payload.numberOfRounds, 1);
    payload.numberOfSections = intOr(payload.numberOfSections, 1);
    payload.entryFee = Math.max(0, floatOr(payload.entryFee, 0));
    payload.winPoints = floatOr(payload.winPoints, 3);
    payload.drawPoints = floatOr(payload.drawPoints, 1);
    payload.lossPoints = floatOr(payload.lossPoints, 0);
    payload.byePoints = floatOr(payload.byePoints, 0);
    payload.pointsPerSetWon = floatOr(payload.pointsPerSetWon, 0);
    payload.pointsPerEndWon = floatOr(payload.pointsPerEndWon, 0);
    if (!payload.scoringMethod) payload.scoringMethod = 'standard';
    // Nothing selected is stored as null rather than [], so "open to everyone"
    // is one value on the way in and on the way back out.
    const allowedMembershipTypes = normalizeAllowedMembershipTypes(payload.allowedMembershipTypes);
    payload.allowedMembershipTypes = allowedMembershipTypes.length ? allowedMembershipTypes : null;
    payload.includeInClubLeaderboard = payload.includeInClubLeaderboard !== false;
    payload.rulesBank = {
      ...DEFAULT_RULES_BANK,
      jackDelivery: rulesBank.jackDelivery === 'place' ? 'place' : 'roll',
      deadEndPolicy: rulesBank.deadEndPolicy === 'dead-end' ? 'dead-end' : 'replay',
      jackOutPolicy: rulesBank.jackOutPolicy === 'respot' ? 'respot' : 'dead-end',
      customRulesNote: typeof rulesBank.customRulesNote === 'string' ? rulesBank.customRulesNote : '',
    };
    payload.appliedRules = appliedRules;
    payload.finalsEnds = (payload.finalsEnds === '' || payload.finalsEnds === null || payload.finalsEnds === undefined)
      ? null
      : intOr(payload.finalsEnds, payload.numberOfEnds || 18);
    payload.quarterFinalEnds = (payload.quarterFinalEnds === '' || payload.quarterFinalEnds === null || payload.quarterFinalEnds === undefined)
      ? null
      : intOr(payload.quarterFinalEnds, payload.numberOfEnds || 18);
    payload.semiFinalEnds = (payload.semiFinalEnds === '' || payload.semiFinalEnds === null || payload.semiFinalEnds === undefined)
      ? null
      : intOr(payload.semiFinalEnds, payload.numberOfEnds || 18);
    payload.finalEnds = (payload.finalEnds === '' || payload.finalEnds === null || payload.finalEnds === undefined)
      ? null
      : intOr(payload.finalEnds, payload.numberOfEnds || 18);

    return payload;
  };

  const combinedRulesCatalog = React.useMemo(() => {
    const generic = normalizeRulesList(rulesLibrary.generic || DEFAULT_GENERIC_RULES, 'site');
    const custom = normalizeRulesList(rulesLibrary.custom || [], 'club');
    return [...generic, ...custom];
  }, [rulesLibrary]);

  const openCompetitionRulesPicker = (context = 'new') => {
    setRulesPickerContext(context);
    if (context === 'edit') setEditingCompSettings(false);
    setViewTracked('competitionRules');
  };

  const currentRulesSelection = rulesPickerContext === 'edit'
    ? normalizeRulesList(compEditForm.appliedRules || [], 'club')
    : normalizeRulesList(compForm.appliedRules || [], 'club');

  const setCurrentRulesSelection = (next) => {
    if (rulesPickerContext === 'edit') {
      setCompEditForm(f => ({ ...f, appliedRules: normalizeRulesList(next, 'club') }));
    } else {
      setCompForm(f => ({ ...f, appliedRules: normalizeRulesList(next, 'club') }));
    }
  };

  const toggleDraftCompetitionRule = (rule, checked) => {
    const current = currentRulesSelection;
    const exists = current.some(r => r.id === rule.id);
    let next = current;
    if (checked && !exists) next = [...current, { id: rule.id, title: rule.title, detail: rule.detail || '', source: rule.source || 'club' }];
    if (!checked && exists) next = current.filter(r => r.id !== rule.id);
    setCurrentRulesSelection(next);
  };

  const printCompetitionRules = (compName, appliedRules) => {
    const list = normalizeRulesList(appliedRules || [], 'club');
    if (!list.length) return;
    const popup = window.open('', '_blank', 'width=900,height=700');
    if (!popup) return;
    const rows = list.map((r, i) => `<li><strong>${i + 1}. ${escapeHtml(r.title)}</strong>${r.detail ? `<div style=\"margin-top:4px\">${escapeHtml(r.detail)}</div>` : ''}</li>`).join('');
    popup.document.write(`<!doctype html><html><head><title>${escapeHtml(compName || 'Competition')} Rules</title><style>body{font-family:Arial,sans-serif;margin:24px;color:#222}h1{margin:0 0 8px}ol{padding-left:20px}li{margin:10px 0}small{color:#666}</style></head><body><h1>${escapeHtml(compName || 'Competition')} Rules</h1><small>Generated ${new Date().toLocaleString()}</small><ol>${rows}</ol></body></html>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const saveCompetitionAppliedRules = async (nextRules, okMessage = 'Rules saved') => {
    if (!selectedComp) return;
    setSavingHomeRules(true);
    try {
      const payload = normalizeCompNumericFields({
        ...selectedComp,
        appliedRules: normalizeRulesList(nextRules || [], 'club'),
      });
      const r = await fetch(`${API}/api/competitions/${selectedComp.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      const updated = await r.json();
      setSelectedComp(updated);
      setComps(prev => prev.map(c => c.id === updated.id ? updated : c));
      refreshClubLeaderboard();
      showToast(okMessage);
    } catch {
      showToast('Failed to save rules', 'red');
    } finally {
      setSavingHomeRules(false);
    }
  };

  const createComp = () => {
    if (!canCreateCompetition) {
      showToast('Select a specific club before creating a competition', 'orange');
      return;
    }
    if (!compForm.name.trim()) { showToast('Please enter a competition name', 'red'); return; }
    const payload = normalizeCompNumericFields({ ...compForm });
    if (!payload.startDate) delete payload.startDate;
    if (!payload.endDate) delete payload.endDate;
    // Assign to selected club (or admin's own club)
    if (selectedClubId) payload.ClubId = selectedClubId;
    else if (currentUser && currentUser.ClubId) payload.ClubId = currentUser.ClubId;
    // Fill in default round start times for any rounds without explicit times
    if (payload.tournamentType === 'round-robin' && payload.numberOfRounds > 0) {
      const rst = { ...(payload.roundStartTimes || {}) };
      const gap = (parseInt(payload.timeLimit) || 60) + 15;
      for (let i = 0; i < parseInt(payload.numberOfRounds); i++) {
        const rn = String(i + 1);
        if (!rst[rn]) {
          const totalMin = 9 * 60 + i * gap;
          rst[rn] = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
        }
      }
      payload.roundStartTimes = rst;
    }
    fetch(`${API}/api/competitions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(c => {
        setComps(p => [c, ...p]);
        showToast(`${c.name} created!`);
        openComp(c);
      })
      .catch(() => showToast('Failed to create competition', 'red'));
  };

  const deleteComp = async (compId) => {
    const targetComp = comps.find(c => c.id === compId);
    if (targetComp && (targetComp.status === 'completed' || targetComp.drawFinalised)) {
      showToast('Locked competitions cannot be deleted', 'amber');
      return;
    }
    if (!(await showConfirm('Delete this competition and all its data?'))) return;
    fetch(`${API}/api/competitions/${compId}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setComps(p => p.filter(c => c.id !== compId));
        if (selectedComp && selectedComp.id === compId) {
          setSelectedComp(null);
          setView('home');
        }
        showToast('Competition deleted');
      })
      .catch(() => showToast('Failed to delete', 'red'));
  };

  const openCompSettings = () => {
    const c = selectedComp;
    setCompEditForm({
      name: c.name || '', format: c.format || 'singles', playersPerTeam: c.playersPerTeam || 1,
      bowlsPerPlayer: c.bowlsPerPlayer || 4, matchType: c.matchType || 'ends',
      numberOfEnds: c.numberOfEnds || 18, targetScore: c.targetScore || 21,
      timeLimit: c.timeLimit || 75, endsPerSet: c.endsPerSet || 9, numberOfSets: c.numberOfSets || 3,
      rinks: c.rinks || 4, numberOfSections: c.numberOfSections || 1,
      availableRinks: c.availableRinks || null,
      selectedGreens: c.selectedGreens || null,
      rinksPerGreen: c.rinksPerGreen || null,
      entryFee: c.entryFee ?? 0,
      winPoints: c.winPoints ?? 2, drawPoints: c.drawPoints ?? 1, lossPoints: c.lossPoints ?? 0,
      byePoints: c.byePoints ?? 2, scoringMethod: c.scoringMethod || 'standard',
      pointsPerSetWon: c.pointsPerSetWon ?? 0, pointsPerEndWon: c.pointsPerEndWon ?? 0,
      includeInClubLeaderboard: c.includeInClubLeaderboard !== false,
      rulesBank: {
        ...DEFAULT_RULES_BANK,
        ...(c.rulesBank || {}),
      },
      appliedRules: normalizeRulesList(c.appliedRules || [], 'club'),
      extraEndOnDraw: c.extraEndOnDraw || false,
      tournamentType: c.tournamentType || 'round-robin',
      drawMethod: c.drawMethod || 'random',
      teamAssignment: c.teamAssignment || 'manual',
      hasFinalsSeries: !!c.hasFinalsSeries,
      finalsFormat: c.finalsFormat || 'semi-final',
      finalsEnds: c.finalsEnds || null,
      quarterFinalEnds: c.quarterFinalEnds ?? c.finalsEnds ?? null,
      semiFinalEnds: c.semiFinalEnds ?? c.finalsEnds ?? null,
      finalEnds: c.finalEnds ?? c.finalsEnds ?? null,
      startDate: c.startDate || '', endDate: c.endDate || '',
      numberOfRounds: c.numberOfRounds || '',
      roundStartTimes: c.roundStartTimes || {},
      roundStartDates: c.roundStartDates || {},
      finalsStartDates: c.finalsStartDates || {},
      finalsStartTimes: c.finalsStartTimes || {},
      registrationClosed: c.registrationClosed || false,
      allowedMembershipTypes: normalizeAllowedMembershipTypes(c.allowedMembershipTypes).length
        ? normalizeAllowedMembershipTypes(c.allowedMembershipTypes)
        : null,
    });
    setSettingsQrData(null);
    setEditingCompSettings(true);
    settingsDrag.current = { active: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 };
    setSettingsPos({ x: 0, y: 0 });
  };

  const showSettingsQR = async (compId) => {
    const baseUrl = PUBLIC_APP_ORIGIN;
    const r = await fetch(`${API}/api/registrations/${compId}/qr?baseUrl=${encodeURIComponent(baseUrl)}`);
    if (r.ok) setSettingsQrData(await r.json());
    else showToast('Failed to load QR code', 'red');
  };

  const saveCompSettings = () => {
    // Fill in default round start times for any rounds without explicit times
    const payload = normalizeCompNumericFields({ ...compEditForm });
    if (payload.tournamentType === 'round-robin' && payload.numberOfRounds > 0) {
      const rst = { ...(payload.roundStartTimes || {}) };
      const gap = (parseInt(payload.timeLimit) || 60) + 15;
      for (let i = 0; i < parseInt(payload.numberOfRounds); i++) {
        const rn = String(i + 1);
        if (!rst[rn]) {
          const totalMin = 9 * 60 + i * gap;
          rst[rn] = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
        }
      }
      payload.roundStartTimes = rst;
    }
    fetch(`${API}/api/competitions/${selectedComp.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(updated => {
        setSelectedComp(updated);
        setComps(p => p.map(c => c.id === updated.id ? updated : c));
        refreshClubLeaderboard();
        setEditingCompSettings(false);
        showToast('Settings saved!');
      })
      .catch(() => showToast('Failed to save settings', 'red'));
  };

  const saveAsTemplate = async () => {
    const label = await showPrompt('Template name:', compForm.name || '');
    if (!label || !label.trim()) return;
    const desc = await showPrompt('Short description (optional):') || '';
    const { name, startDate, endDate, ...settings } = compForm;
    const templateClubId = isSuperAdmin && !selectedClubId ? null : (selectedClubId || (currentUser && currentUser.ClubId));
    fetch(`${API}/api/templates`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: label.trim(), desc, icon: 'fa-bookmark', settings, ClubId: templateClubId }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(t => { setCustomTemplates(prev => [...prev, t]); showToast(`Template "${t.label}" saved!`); })
      .catch(() => showToast('Failed to save template', 'red'));
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    if (!(await showConfirm('Delete this template?'))) return;
    fetch(`${API}/api/templates/${id}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setCustomTemplates(prev => prev.filter(t => t.id !== id)); showToast('Template deleted'); })
      .catch(() => showToast('Failed to delete template', 'red'));
  };

  /* --- Player management --- */
  // Quick-add: create player AND register in competition
  const quickAddPlayer = () => {
    const name = quickName.trim();
    if (!name || !selectedComp) return;
    fetch(`${API}/api/competitions/${selectedComp.id}/players/quick`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(user => {
        setCompPlayers(prev => [...prev, user]);
        setUsers(prev => [...prev, user]);
        setQuickName('');
        if (quickRef.current) quickRef.current.focus();
        const defaultClubId = user.ClubId || selectedComp.ClubId || (currentUser ? currentUser.ClubId : '') || '';
        setQuickAddDetails({
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          ClubId: defaultClubId ? String(defaultClubId) : '',
        });
      })
      .catch(() => showToast('Failed to add player', 'red'));
  };

  const saveQuickAddDetails = () => {
    if (!quickAddDetails || !quickAddDetails.id) return;
    const payload = {
      name: (quickAddDetails.name || '').trim(),
      email: (quickAddDetails.email || '').trim(),
      ClubId: quickAddDetails.ClubId ? parseInt(quickAddDetails.ClubId) : null,
    };
    if (!payload.name) { showToast('Name is required', 'red'); return; }
    setQuickAddSaving(true);
    fetch(`${API}/api/users/${quickAddDetails.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(updated => {
        setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
        setCompPlayers(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
        setQuickAddDetails(null);
        showToast('Player details saved');
      })
      .catch(() => showToast('Failed to save player details', 'red'))
      .finally(() => setQuickAddSaving(false));
  };

  // Add existing player from the club database
  const [existingPlayerId, setExistingPlayerId] = useState('');
  const addExistingPlayer = () => {
    if (!existingPlayerId || !selectedComp) return;
    fetch(`${API}/api/competitions/${selectedComp.id}/players`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: parseInt(existingPlayerId) }),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(user => {
        if (user && user.name) setCompPlayers(prev => [...prev, user]);
        setExistingPlayerId('');
        setMatches([]);
      })
      .catch(() => showToast('Failed to add player', 'red'));
  };

  const removePlayer = (userId) => {
    fetch(`${API}/api/competitions/${selectedComp.id}/players/${userId}`, { method: 'DELETE' })
      .then(r => {
        if (!r.ok) throw new Error('Failed to remove player');
        setCompPlayers(prev => prev.filter(p => p.id !== userId));
        // Refresh teams and matches since backend cleans up teams & clears draw
        fetch(`${API}/api/competitions/${selectedComp.id}/teams`).then(r => r.json()).then(setTeams).catch(() => {});
        setMatches([]);
      })
      .catch(() => {
        showToast('Failed to remove player', 'red');
        // Re-fetch actual state from server
        fetch(`${API}/api/competitions/${selectedComp.id}/players`).then(r => r.json()).then(setCompPlayers).catch(() => {});
      });
  };

  const togglePaid = async (userId) => {
    try {
      /* A refusal used to come back as { error: ... }, so data.hasPaid was
         undefined and the tick was quietly set to "not paid" — a wrong
         answer that looked like a real one until somebody refreshed. */
      const data = await apiJson(`/api/competitions/${selectedComp.id}/players/${userId}/paid`, { method: 'PATCH' });
      setCompPlayers(prev => prev.map(p => p.id === userId ? { ...p, hasPaid: data.hasPaid } : p));
    } catch (err) {
      showToast(err.message || 'Could not update paid status', 'red');
    }
  };

  // Add all club players at once
  const addAllPlayers = () => {
    const unregistered = users.filter(u => !compPlayers.find(p => p.id === u.id));
    if (unregistered.length === 0) return;
    fetch(`${API}/api/competitions/${selectedComp.id}/players/batch`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds: unregistered.map(u => u.id) }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(players => { setCompPlayers(players); setMatches([]); showToast(`Added ${unregistered.length} players`); })
      .catch(() => showToast('Failed to add players', 'red'));
  };

  /* --- Team management --- */
  const assignedPlayerIds = teams.flatMap(t => (t.Users || []).map(u => u.id));
  const availableForTeam = compPlayers.filter(p => !assignedPlayerIds.includes(p.id));
  const compDisciplineMap = { pairs: 'pairs', triples: 'triples', fours: 'fours', '2-4-2-pairs': 'pairs' };
  const disciplinePlayerLimits = { singles: 1, pairs: 2, triples: 3, fours: 4 };
  const selectedCompDiscipline = compDisciplineMap[selectedComp?.format] || null;
  const importableClubTeams = clubTeams
    .filter(ct => !selectedCompDiscipline || ct.discipline === selectedCompDiscipline)
    .filter(ct => !teams.find(t => t.ClubTeamId === ct.id));

  const importClubTeam = async (clubTeamId) => {
    if (!clubTeamId) return;
    const ct = clubTeams.find(t => t.id === parseInt(clubTeamId));
    if (!ct) return;
    if (selectedCompDiscipline && ct.discipline !== selectedCompDiscipline) {
      showToast(`Only ${selectedCompDiscipline} club teams can be imported for this competition`, 'amber');
      return;
    }
    // Get active players from the club team
    const activePlayers = (ct.Users || []).filter(u => u.ClubTeamPlayer && u.ClubTeamPlayer.active);
    const playerIds = activePlayers.map(u => u.id);
    // First, auto-register any club team members not yet in the competition
    const unregistered = activePlayers.filter(u => !compPlayers.find(p => p.id === u.id));

    /* Every one of these used to go unchecked, and the import reported
        "Imported <team> (4 players)" whatever came back — so a club at its
        plan's member limit, or a player already entered, produced a success
        message and an { error: ... } object pushed into `teams`, which the
        next render tried to iterate.

        allSettled, because one player failing to register should not abandon
        an import of four: the ones that worked are kept, and the team is
        still made. What cannot be glossed over is the team itself failing. */
    const registrations = await Promise.allSettled(unregistered.map(u =>
      apiJson(`/api/competitions/${selectedComp.id}/players`, {
        method: 'POST', body: { userId: u.id },
      })
    ));
    const registered = registrations
      .filter(r => r.status === 'fulfilled' && r.value && r.value.id)
      .map(r => r.value);
    if (registered.length > 0) {
      setCompPlayers(prev => [...prev, ...registered.filter(p => !prev.find(x => x.id === p.id))]);
    }
    const refused = registrations.length - registered.length;

    // Now create the competition team linked to the club team
    try {
      const t = await apiJson(`/api/competitions/${selectedComp.id}/teams`, {
        method: 'POST', body: { name: ct.name, ClubTeamId: ct.id, playerIds },
      });
      setTeams(prev => [...prev, t]);
      setMatches([]);
      showToast(
        refused
          ? `Imported ${ct.name}, but ${refused} player(s) could not be entered`
          : `Imported ${ct.name} (${playerIds.length} players)`,
        refused ? 'amber' : 'green',
      );
    } catch (err) {
      showToast(err.message || `Could not import ${ct.name}`, 'red');
    }
  };

  const generateTeams = async () => {
    if (compPlayers.length === 0) { showToast('Register players first', 'amber'); return; }
    try {
      /* apiList, not apiJson: a refusal here used to be written into `teams`
         as { error: ... }, and the next render called teams.map. */
      const generated = await apiList(`/api/competitions/${selectedComp.id}/teams/generate`, { method: 'POST' });
      setTeams(generated);
      setMatches([]);
      showToast('Teams generated!');
    } catch (err) {
      showToast(err.message || 'Could not generate teams', 'red');
    }
  };

  /* Two requests in a row, neither of which used to be checked.
   *
     A refused club team came back as { error: ... } and was pushed into
     clubTeams as though it were a team, and its undefined id was then sent
     as the ClubTeamId of a competition team — so one refusal produced a
     bogus row in the club's team list and a second request built on a team
     that did not exist. Nothing was reported either way.
   *
     The club team is created first because the competition team refers to
     it, so if the first step fails the second is not attempted. */
  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    const name = newTeamName.trim();
    // Map competition format to club team discipline
    const discipline = compDisciplineMap[selectedComp.format] || 'pairs';
    const defaultClubId = selectedComp?.ClubId || (currentUser ? currentUser.ClubId : undefined);

    let ct;
    try {
      ct = await apiJson('/api/clubteams', {
        method: 'POST',
        body: { name, discipline, ClubId: defaultClubId || undefined },
      });
    } catch (err) {
      showToast(err.message || 'Could not create that team', 'red');
      return;
    }
    setClubTeams(prev => [...asClubTeamsArray(prev), ct]);

    let t;
    try {
      t = await apiJson(`/api/competitions/${selectedComp.id}/teams`, {
        method: 'POST', body: { name, ClubTeamId: ct.id },
      });
    } catch (err) {
      /* The club team was made and is real — it is in the club's list and
         will be there on the next visit. Only its entry into this
         competition failed, which is what to say. */
      showToast(err.message || `"${name}" was created, but could not be entered into this competition`, 'red');
      return;
    }

    setTeams(prev => [...prev, t]);
    setQuickTeamDetails({
      id: ct.id,
      compTeamId: t.id,
      name: ct.name || name,
      discipline: ct.discipline || discipline,
      ClubId: String(ct.ClubId || defaultClubId || ''),
    });
    setQuickTeamPlayerId('');
    setQuickTeamNewPlayerName('');
    setNewTeamName('');
    setMatches([]);
  };

  const saveQuickTeamDetails = () => {
    if (!quickTeamDetails || !quickTeamDetails.id) return;
    const payload = {
      name: (quickTeamDetails.name || '').trim(),
      discipline: quickTeamDetails.discipline || 'pairs',
      ClubId: quickTeamDetails.ClubId ? parseInt(quickTeamDetails.ClubId) : null,
    };
    if (!payload.name) { showToast('Team name is required', 'red'); return; }
    setQuickTeamSaving(true);
    fetch(`${API}/api/clubteams/${quickTeamDetails.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(updated => {
        setClubTeams(prev => asClubTeamsArray(prev).map(t => t.id === updated.id ? { ...t, ...updated } : t));
        setTeams(prev => prev.map(t => t.ClubTeamId === updated.id ? { ...t, name: updated.name || t.name } : t));
        setQuickTeamDetails(null);
        showToast('Team details saved');
      })
      .catch(() => showToast('Failed to save team details', 'red'))
      .finally(() => setQuickTeamSaving(false));
  };

  const addExistingPlayerToQuickTeam = (pickedUserId) => {
    const userId = pickedUserId || quickTeamPlayerId;
    if (!quickTeamDetails || !quickTeamDetails.compTeamId || !userId) return;
    const team = teams.find(t => t.id === quickTeamDetails.compTeamId);
    const members = (team && team.Users) ? team.Users : [];
    const maxPlayers = disciplinePlayerLimits[quickTeamDetails.discipline] || 2;
    if (members.length >= maxPlayers) {
      showToast(`This ${quickTeamDetails.discipline} team is full`, 'amber');
      return;
    }
    setQuickTeamAddingPlayer(true);
    fetch(`${API}/api/competitions/${selectedComp.id}/teams/${quickTeamDetails.compTeamId}/players`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: parseInt(userId) }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const updated = data.team || data;
        setTeams(prev => prev.map(t => t.id === updated.id ? updated : t));
        if (data.registered && data.user) {
          setCompPlayers(prev => prev.find(p => p.id === data.user.id) ? prev : [...prev, data.user]);
        }
        setQuickTeamPlayerId('');
      })
      .catch(() => showToast('Failed to add player', 'red'))
      .finally(() => setQuickTeamAddingPlayer(false));
  };

  const createAndAddPlayerToQuickTeam = () => {
    const name = quickTeamNewPlayerName.trim();
    if (!quickTeamDetails || !quickTeamDetails.compTeamId || !name) return;
    const team = teams.find(t => t.id === quickTeamDetails.compTeamId);
    const members = (team && team.Users) ? team.Users : [];
    const maxPlayers = disciplinePlayerLimits[quickTeamDetails.discipline] || 2;
    if (members.length >= maxPlayers) {
      showToast(`This ${quickTeamDetails.discipline} team is full`, 'amber');
      return;
    }
    setQuickTeamAddingPlayer(true);
    fetch(`${API}/api/competitions/${selectedComp.id}/players/quick`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(user => {
        setUsers(prev => prev.find(u => u.id === user.id) ? prev : [...prev, user]);
        setCompPlayers(prev => prev.find(p => p.id === user.id) ? prev : [...prev, user]);
        const defaultClubId = user.ClubId || selectedComp.ClubId || (currentUser ? currentUser.ClubId : '') || '';
        setQuickAddDetails({
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          ClubId: defaultClubId ? String(defaultClubId) : '',
        });
        return fetch(`${API}/api/competitions/${selectedComp.id}/teams/${quickTeamDetails.compTeamId}/players`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
      })
      .then(data => {
        const updated = data.team || data;
        setTeams(prev => prev.map(t => t.id === updated.id ? updated : t));
        setQuickTeamNewPlayerName('');
      })
      .catch(() => showToast('Failed to create/add player', 'red'))
      .finally(() => setQuickTeamAddingPlayer(false));
  };

  const deleteTeam = (teamId) => {
    fetch(`${API}/api/competitions/${selectedComp.id}/teams/${teamId}`, { method: 'DELETE' })
      .then(r => {
        if (!r.ok) throw new Error('Failed to delete team');
        setTeams(prev => prev.filter(t => t.id !== teamId));
        setMatches([]);
      })
      .catch(() => {
        showToast('Failed to delete team', 'red');
        fetch(`${API}/api/competitions/${selectedComp.id}/teams`).then(r => r.json()).then(setTeams).catch(() => {});
      });
  };

  const addPlayerToTeam = (teamId, userId) => {
    if (!userId) return;
    fetch(`${API}/api/competitions/${selectedComp.id}/teams/${teamId}/players`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: parseInt(userId) }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const updated = data.team || data; // handle new response shape
        setTeams(prev => prev.map(t => t.id === updated.id ? updated : t));
        // If player was auto-registered, add to compPlayers
        if (data.registered && data.user) {
          setCompPlayers(prev => prev.find(p => p.id === data.user.id) ? prev : [...prev, data.user]);
        }
        setTeamPlayerPick('');
        setMatches([]);
      })
      .catch(() => showToast('Failed to add to team', 'red'));
  };

  const removePlayerFromTeam = async (teamId, userId) => {
    try {
      /* A refused DELETE used to come back as { error: ... }, whose .id is
         undefined, so no team matched and the row simply stayed where it was
         — the button looked broken rather than refused. */
      const updated = await apiJson(`/api/competitions/${selectedComp.id}/teams/${teamId}/players/${userId}`, { method: 'DELETE' });
      setTeams(prev => {
        const next = prev.map(t => t.id === updated.id ? updated : t);
        // Remove player from compPlayers if they no longer appear in any team
        const allTeamPlayerIds = new Set(next.flatMap(t => (t.Users || []).map(u => u.id)));
        if (!allTeamPlayerIds.has(userId)) {
          setCompPlayers(prevPlayers => prevPlayers.filter(p => p.id !== userId));
        }
        return next;
      });
      setMatches([]);
    } catch (err) {
      showToast(err.message || 'Could not remove that player from the team', 'red');
    }
  };

  const removePlayerFromQuickTeam = (userId) => {
    if (!quickTeamDetails || !quickTeamDetails.compTeamId || !userId) return;
    removePlayerFromTeam(quickTeamDetails.compTeamId, userId);
  };

  const renameTeam = async (teamId, name) => {
    if (!name.trim()) return;
    try {
      /* The rename used to close the editor and revert on screen with no
         message when the server refused — a duplicate name being the likely
         reason, and exactly the thing somebody needs telling about. The
         editor is left open on failure so the name can be corrected. */
      const updated = await apiJson(`/api/competitions/${selectedComp.id}/teams/${teamId}`, {
        method: 'PUT', body: { name },
      });
      setTeams(prev => prev.map(t => t.id === updated.id ? updated : t));
      setEditingTeam(null);
      // Re-fetch matches and leaderboard so the new team name shows everywhere
      const [refreshedMatches, refreshedBoard] = await Promise.allSettled([
        apiList(`/api/competitions/${selectedComp.id}/matches`),
        apiJson(`/api/competitions/${selectedComp.id}/leaderboard`),
      ]);
      if (refreshedMatches.status === 'fulfilled') setMatches(refreshedMatches.value);
      if (refreshedBoard.status === 'fulfilled') setLeaderboard(refreshedBoard.value);
    } catch (err) {
      showToast(err.message || 'Could not rename that team', 'red');
    }
  };

  /* --- Fixtures --- */
  /* The .then here used to run whether or not the delete worked — fetch only
     rejects when there is no response at all — so a refusal cleared every
     match off the screen and said "Draw deleted" while the server still had
     the lot. They came back on the next refresh, and in between an admin
     could start building a second draw over the top of the first.

     Now the draw stays on screen unless the server actually deleted it. */
  const deleteDraw = async () => {
    if (!(await showConfirm('Delete all matches and reset the draw? This cannot be undone.'))) return;
    try {
      await apiJson(`/api/competitions/${selectedComp.id}/matches`, { method: 'DELETE' });
      setMatches([]);
      // For singles, also clean up stale auto-generated teams and refresh
      if (selectedComp.format === 'singles') {
        try {
          setTeams(await apiList(`/api/competitions/${selectedComp.id}/teams`));
        } catch { /* The draw is gone, which is what was asked; the team list
                     catches up on the next refresh. */ }
      }
      showToast('Draw deleted');
    } catch (err) {
      showToast(err.message || 'Could not delete the draw', 'red');
    }
  };

  const generateFixtures = () => {
    if (teams.length < 2) { showToast('Need at least 2 teams', 'amber'); return; }
    fetch(`${API}/api/competitions/${selectedComp.id}/generate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startTime: '09:00', minutesBetween: 75 }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(m => { setMatches(m); showToast('Fixtures generated!'); })
      .catch(() => showToast('Failed to generate fixtures', 'red'));
  };

  const generateFinals = () => {
    fetch(`${API}/api/competitions/${selectedComp.id}/generate-finals`, { method: 'POST' })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) { showToast(data.error || 'Failed to generate finals', 'red'); return; }
        setMatches(prev => {
          const nonFinals = prev.filter(m => m.bracketType !== 'finals');
          return [...nonFinals, ...data];
        });
        showToast('Finals draw generated!');
      })
      .catch(() => showToast('Failed to generate finals', 'red'));
  };

  /* --- Manual match creation --- */
  const addManualMatch = () => {
    if (!manualMatch.homeTeamId || !manualMatch.awayTeamId) { showToast('Select both teams', 'amber'); return; }
    if (manualMatch.homeTeamId === manualMatch.awayTeamId) { showToast('Teams must be different', 'amber'); return; }
    const isDE = (selectedComp.tournamentType || 'round-robin') === 'double-elimination';
    fetch(`${API}/api/competitions/${selectedComp.id}/matches`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        round: manualMatch.round || 1,
        homeTeamId: parseInt(manualMatch.homeTeamId),
        awayTeamId: parseInt(manualMatch.awayTeamId),
        rink: manualMatch.rink ? parseInt(manualMatch.rink) : null,
        green: manualMatch.green ? parseInt(manualMatch.green) : null,
        time: manualMatch.time || null,
        bracketType: isDE ? 'winners' : null,
        bracketRound: isDE ? (manualMatch.round || 1) : null,
      }),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(m => {
        setMatches(prev => [...prev, m]);
        setManualMatch(prev => ({ ...prev, homeTeamId: '', awayTeamId: '' }));
        showToast('Match added!');
      })
      .catch(() => showToast('Failed to add match', 'red'));
  };

  const deleteMatch = async (matchId) => {
    if (!(await showConfirm('Delete this match?'))) return;
    fetch(`${API}/api/matches/${matchId}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setMatches(prev => prev.filter(m => m.id !== matchId));
        showToast('Match deleted');
      })
      .catch(() => showToast('Failed to delete match', 'red'));
  };

  /* --- Score submission --- */
  const submitScore = (matchId, data) => {
    if (data.notPlayed) {
      fetch(`${API}/api/matches/${matchId}/score`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ homeScore: 0, awayScore: 0, result: 'not-played', completed: true }),
      }).then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Failed'); }); return r.json(); }).then(() => {
        setScoringMatch(null);
        showToast('Match marked as not played', 'orange');
        refreshComp();
      }).catch(err => showToast(err.message || 'Failed to mark not played', 'red'));
      return;
    }
    if (data.forfeit) {
      fetch(`${API}/api/matches/${matchId}/score`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ homeScore: 0, awayScore: 0, result: data.forfeit, completed: true }),
      }).then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Failed'); }); return r.json(); }).then(() => {
        setScoringMatch(null);
        showToast('Forfeit recorded', 'orange');
        refreshComp();
      }).catch(err => showToast(err.message || 'Failed to record forfeit', 'red'));
      return;
    }
    fetch(`${API}/api/matches/${matchId}/score`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, completed: true }),
    }).then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error || 'Failed'); }); return r.json(); }).then(() => {
      setScoringMatch(null);
      showToast('Score saved!');
      refreshComp();
    }).catch(err => showToast(err.message || 'Failed to save score', 'red'));
  };

  /* --- Clear / reset a saved score --- */
  const clearScore = (matchId) => {
    fetch(`${API}/api/matches/${matchId}/score`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ homeScore: 0, awayScore: 0, homeEndsWon: 0, awayEndsWon: 0, homeSetsWon: 0, awaySetsWon: 0, endScores: null, setScores: null, result: null, completed: false }),
    }).then(r => {
      if (!r.ok) return r.text().then(t => { throw new Error(t); });
      return r.json();
    }).then(() => {
      showToast('Score reset to Pending');
      refreshComp();
    }).catch(err => {
      console.error('clearScore error:', err);
      showToast('Failed to clear score', 'red');
    });
  };

  /* --- Player score submission (requires admin approval) --- */
  const submitPlayerScore = (matchId, data) => {
    fetch(`${API}/api/matches/${matchId}/player-score`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => {
      if (!r.ok) return r.text().then(t => { throw new Error(t); });
      return r.json();
    }).then(() => {
      setScoringMatch(null);
      showToast('Score submitted for approval');
      refreshComp();
    }).catch(err => {
      console.error('submitPlayerScore error:', err);
      showToast('Failed to submit score', 'red');
    });
  };

  /* --- Approve / reject player-submitted score --- */
  const approveScore = (matchId, approved) => {
    fetch(`${API}/api/matches/${matchId}/approve-score`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved }),
    }).then(r => {
      if (!r.ok) return r.text().then(t => { throw new Error(t); });
      return r.json();
    }).then(() => {
      showToast(approved ? 'Score approved' : 'Score rejected');
      refreshComp();
    }).catch(err => {
      console.error('approveScore error:', err);
      showToast('Failed', 'red');
    });
  };

  /* --- Check if current user is in a match --- */
  const isPlayerInMatch = (m) => {
    if (!currentUser) return false;
    const homeUsers = m.HomeTeam ? m.HomeTeam.Users || [] : [];
    const awayUsers = m.AwayTeam ? m.AwayTeam.Users || [] : [];
    return homeUsers.some(u => u.id === currentUser.id) || awayUsers.some(u => u.id === currentUser.id);
  };

  /* --- Helpers --- */
  const completedMatches = matches.filter(m => m.completed && m.result !== 'bye' && m.result !== 'not-played').length;
  const totalPlayable = matches.filter(m => m.result !== 'bye' && m.result !== 'not-played').length;
  const isFinalised = selectedComp && selectedComp.status === 'completed';
  const isReopened = selectedComp && selectedComp.status === 'reopened';
  const isLocked = isFinalised || isReopened || (selectedComp && selectedComp.drawFinalised);
  const appliedRulesForHome = normalizeRulesList((selectedComp && selectedComp.appliedRules) || [], 'club');
  const standardRulesForHome = normalizeRulesList((rulesLibrary && rulesLibrary.generic) || DEFAULT_GENERIC_RULES, 'site');
  const displayRulesForHome = appliedRulesForHome.length > 0 ? appliedRulesForHome : standardRulesForHome;
  const teamMemberIds = new Set(teams.flatMap(t => (t.Users || []).map(u => u.id)));
  const teamCompetitionPlayers = compPlayers.filter(p => teamMemberIds.has(p.id));
  const emailSelectablePlayers = (activeStep === 'teams' && selectedComp && selectedComp.format !== 'singles')
    ? teamCompetitionPlayers
    : compPlayers;
  const competitionPlayersWithEmail = emailSelectablePlayers.filter(p => !!p.email);
  const allEmailPlayersSelected = competitionPlayersWithEmail.length > 0 && selectedCompPlayerIds.length === competitionPlayersWithEmail.length;
  const manualCompEmailTemplates = competitionEmailTemplates.filter(t => t.type !== 'welcome' && t.type !== 'registration');
  const hasEntryConfirmationTemplate = competitionEmailTemplates.some(t => t.type === 'registration');

  /* --- Shared profile-content renderers (used in split-pane AND standalone views) --- */
  const openMemberIdPage = useCallback(() => {
    if (!currentUser) return;
    setViewTracked('memberId');
  }, [currentUser]);

  const playerProfileEl = <PlayerProfileContent clubs={clubs} confirmMembershipForProfile={confirmMembershipForProfile} currentUser={currentUser} deletePlayer={deletePlayer} editingProfile={editingProfile} getMembershipRouteRef={getMembershipRouteRef} isAdmin={isAdmin} matches={matches} memberQrData={memberQrData} openComp={openComp} openTeamProfile={openTeamProfile} playerProfile={playerProfile} profileForm={profileForm} resetPlayerPassword={resetPlayerPassword} resetPwOpen={resetPwOpen} resetPwValue={resetPwValue} saveProfile={saveProfile} setEditingProfile={setEditingProfile} setMembershipRoute={setMembershipRoute} setPlayerProfile={setPlayerProfile} setProfileForm={setProfileForm} setResetPwOpen={setResetPwOpen} setResetPwValue={setResetPwValue} showToast={showToast} teams={teams} users={users} />;

  const teamProfileEl = <TeamProfileContent addPlayerToClubTeam={addPlayerToClubTeam} setUsers={setUsers} showToast={showToast} clubs={clubs} comps={comps} deleteClubTeam={deleteClubTeam} editingTeamProfile={editingTeamProfile} isAdmin={isAdmin} matches={matches} openComp={openComp} openProfile={openProfile} removePlayerFromClubTeam={removePlayerFromClubTeam} saveTeamProfile={saveTeamProfile} setEditingTeamProfile={setEditingTeamProfile} setTeamProfile={setTeamProfile} setTeamProfileForm={setTeamProfileForm} teamProfile={teamProfile} teamProfileForm={teamProfileForm} togglePlayerActive={togglePlayerActive} users={users} />;

  /* ================================================================
     RENDER
     ================================================================ */

  /* --- AUTH SCREENS --- */
  if (authLoading) {
    return (
      <div className="w3-display-middle w3-center">
        <TrophySvg size={64} color="green" className="logo-pulse" />
        <p className="w3-text-grey" style={{ marginTop: 12 }}>Loading...</p>
      </div>
    );
  }

  if (needsSetup) {
    return (<SetupScreen onSetup={doSeedAdmin} />);
  }

  if (resetToken) {
    const leaveLinkPage = () => { setResetToken(null); window.history.replaceState(null, '', '/'); };
    return (<ResetPasswordScreen API={API} token={resetToken} onDone={leaveLinkPage}
      onSignedIn={(data) => {
        // Signed in by the emailed link, exactly as a password login would.
        localStorage.setItem('greensheet_token', data.token);
        setAuthToken(data.token);
        setCurrentUser(data.user);
        leaveLinkPage();
      }} />);
  }

  if (membershipRoute && membershipRoute.clubRef) {
    return (
      <MembershipPortalScreen
        API={API}
        clubRef={membershipRoute.clubRef}
        intent={membershipRoute.intent}
        currentUser={currentUser}
        onDone={() => {
          setMembershipRoute(null);
          window.history.replaceState({}, '', currentUser ? '/' : '/login');
          if (currentUser) setViewTracked('home');
        }}
      />
    );
  }

  if (regCompId && !isLoggedIn) {
    return (<AuthScreen mode="register" onLogin={doLogin} onRegister={doRegister} regCompId={regCompId} />);
  }

  if (!isLoggedIn) {
    return (
      <div className="page-container-md" style={{ paddingTop: '60px' }}>
        <AuthScreen mode={authView} onLogin={async (e, p) => { await doLogin(e, p); }}
          onRegister={async (n, e, p, cId, membershipData, membershipIntent) => {
            return doRegister(n, e, p, cId, membershipData, membershipIntent);
          }}
          onSwitch={setAuthView} />
      </div>
    );
  }

  const openPendingApproval = () => {
    if (pendingRegCount > 0) {
      setShowPendingRegsModal(true);
    } else {
      setViewTracked('adminUsers');
    }
  };

  /* The club screen replaces the whole window rather than sitting inside the
     app shell: it goes on a TV, where a nav bar is chrome nobody can use and
     a footer of links is a waste of the only thing the screen has to give. */
  if (view === 'clubScreen' && isAdmin) {
    return (
      <ClubScreenView
        clubs={clubs} comps={comps} dashLeaderboard={dashLeaderboard}
        recentMatches={recentMatches} selectedClubId={selectedClubId} upcomingMatches={upcomingMatches}
        settings={loadScreenSettings()}
        onExit={() => setViewTracked('clubScreenControl')} />
    );
  }

  return (
    <div className={(view === 'competition' && activeStep === 'home') ? 'comp-home-lock' : undefined}>
      {/* Toast */}
      {toast && (
        <div className={`w3-panel w3-${toast.type} w3-card-4 w3-animate-top toast-bar`}>
          <p>{toast.msg}</p>
        </div>
      )}

      {/* Pending Registrations Modal */}
      {showPendingRegsModal && (
        <PendingRegsModal API={API} authToken={authToken}
          onClose={() => setShowPendingRegsModal(false)}
          onRegChange={() => { refreshPendingCounts(); }}
          onOpenComp={(compId) => {
            const comp = comps.find(c => c.id === compId);
            if (comp) { openComp(comp); setShowPendingRegsModal(false); }
          }}
        />
      )}

      {/* Custom Confirm Dialog */}
      {confirmState && (
        <div className="confirm-overlay" onClick={handleConfirmCancel}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <i className="fa fa-exclamation-triangle"></i> Confirm
            </div>
            <div className="confirm-dialog-body">{confirmState.message}</div>
            <div className="confirm-dialog-actions">
              <button className="confirm-btn-cancel" onClick={handleConfirmCancel}>Cancel</button>
              <button className="confirm-btn-ok" onClick={handleConfirmOk}>OK</button>
            </div>
          </div>
        </div>
      )}

      {promptState && (
        <div className="confirm-overlay" onClick={handlePromptCancel}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <i className="fa fa-pencil"></i> Input
            </div>
            <div className="confirm-dialog-body">
              <p style={{ margin: '0 0 8px' }}>{promptState.message}</p>
              <input className="w3-input w3-border w3-round" autoFocus value={promptValue}
                onChange={e => setPromptValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePromptOk()} />
            </div>
            <div className="confirm-dialog-actions">
              <button className="confirm-btn-cancel" onClick={handlePromptCancel}>Cancel</button>
              <button className="confirm-btn-ok" onClick={handlePromptOk}>OK</button>
            </div>
          </div>
        </div>
      )}

      {quickAddDetails && (
        <div className="w3-modal" style={{ display: 'block' }}>
          <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '520px' }}>
            <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0 }}><i className="fa fa-user-plus w3-margin-right"></i>New Player Details</h5>
              <button className="w3-button w3-round" onClick={() => setQuickAddDetails(null)} title="Close">&times;</button>
            </div>
            <div className="w3-container w3-padding">
              <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
                Add email and confirm club for this player.
              </p>

              <label className="w3-small w3-text-grey">Name</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom"
                value={quickAddDetails.name}
                onChange={e => setQuickAddDetails(prev => ({ ...prev, name: e.target.value }))} />

              <label className="w3-small w3-text-grey">Email</label>
              <input className="w3-input w3-border w3-round w3-margin-bottom" autoFocus
                type="email"
                placeholder="player@example.com"
                value={quickAddDetails.email}
                onChange={e => setQuickAddDetails(prev => ({ ...prev, email: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && saveQuickAddDetails()} />

              <label className="w3-small w3-text-grey">Club</label>
              <select className="w3-select w3-border w3-round"
                disabled={!isSuperAdmin}
                value={quickAddDetails.ClubId || ''}
                onChange={e => setQuickAddDetails(prev => ({ ...prev, ClubId: e.target.value }))}>
                <option value="">No club</option>
                {clubs.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button className="w3-button w3-theme w3-round" onClick={saveQuickAddDetails} disabled={quickAddSaving}>
                  <i className="fa fa-save w3-margin-right"></i>{quickAddSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {quickTeamDetails && !quickAddDetails && (
        <div className="w3-modal" style={{ display: 'block' }}>
          <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '1050px', width: 'calc(100% - 20px)' }}>
            <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0 }}><i className="fa fa-group w3-margin-right"></i>New Team Details</h5>
              <button className="w3-button w3-round" onClick={() => setQuickTeamDetails(null)} title="Close">&times;</button>
            </div>
            <div className="w3-container w3-padding">
              <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
                Confirm team profile details.
              </p>
              {(() => {
                const team = teams.find(t => t.id === quickTeamDetails.compTeamId);
                const members = (team && team.Users) ? team.Users : [];
                const memberIds = new Set(members.map(u => u.id));
                const availableUsers = users.filter(u => !memberIds.has(u.id));
                const maxPlayers = disciplinePlayerLimits[quickTeamDetails.discipline] || 2;
                const teamFull = members.length >= maxPlayers;
                return (
                  <div className="quick-team-modal-grid">
                    <div className="quick-team-modal-col">
                      <label className="w3-small w3-text-grey">Team name</label>
                      <input className="w3-input w3-border w3-round w3-margin-bottom" autoFocus
                        value={quickTeamDetails.name}
                        onChange={e => setQuickTeamDetails(prev => ({ ...prev, name: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && saveQuickTeamDetails()} />

                      <label className="w3-small w3-text-grey">Discipline</label>
                      <select className="w3-select w3-border w3-round w3-margin-bottom"
                        value={quickTeamDetails.discipline}
                        onChange={e => setQuickTeamDetails(prev => ({ ...prev, discipline: e.target.value }))}>
                        <option value="pairs">Pairs</option>
                        <option value="triples">Triples</option>
                        <option value="fours">Fours</option>
                      </select>

                      <label className="w3-small w3-text-grey">Club</label>
                      <select className="w3-select w3-border w3-round"
                        disabled={!isSuperAdmin}
                        value={quickTeamDetails.ClubId || ''}
                        onChange={e => setQuickTeamDetails(prev => ({ ...prev, ClubId: e.target.value }))}>
                        <option value="">No club</option>
                        {clubs.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="quick-team-modal-col">
                      <label className="w3-small w3-text-grey" style={{ display: 'block' }}>Add existing player</label>
                      <select className="w3-select w3-border w3-round"
                        disabled={quickTeamAddingPlayer || teamFull}
                        value={quickTeamPlayerId}
                        onChange={e => {
                          const val = e.target.value;
                          setQuickTeamPlayerId(val);
                          if (val) addExistingPlayerToQuickTeam(val);
                        }}>
                        <option value="">Select player...</option>
                        {availableUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>

                      <label className="w3-small w3-text-grey" style={{ display: 'block', marginTop: '10px' }}>Create new player and add</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input className="w3-input w3-border w3-round" style={{ flex: 1 }}
                          disabled={teamFull}
                          placeholder="Player name"
                          value={quickTeamNewPlayerName}
                          onChange={e => setQuickTeamNewPlayerName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && createAndAddPlayerToQuickTeam()} />
                        <button className="w3-button w3-theme-l3 w3-round" onClick={createAndAddPlayerToQuickTeam} disabled={quickTeamAddingPlayer || teamFull || !quickTeamNewPlayerName.trim()}>
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                      <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                        {teamFull ? `Player limit reached for ${quickTeamDetails.discipline}.` : `Up to ${maxPlayers} player${maxPlayers !== 1 ? 's' : ''} for ${quickTeamDetails.discipline}.`}
                      </p>
                    </div>

                    <div className="quick-team-modal-col">
                      <label className="w3-small w3-text-grey" style={{ display: 'block' }}>
                        Team players ({members.length}/{maxPlayers})
                      </label>
                      {members.length === 0 ? (
                        <p className="w3-small w3-text-grey" style={{ margin: '4px 0 0' }}><em>No players added yet</em></p>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {members.map(u => (
                            <span key={u.id} className="w3-tag w3-round w3-theme-l4" style={{ padding: '4px 6px 4px 10px', fontSize: 'var(--gs-text-sm)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <span>
                                <i className="fa fa-user w3-margin-right"></i>{u.name}
                              </span>
                              <button
                                type="button"
                                className="w3-button w3-tiny w3-hover-red"
                                style={{ padding: '0 4px', lineHeight: 1.2 }}
                                onClick={() => removePlayerFromQuickTeam(u.id)}
                                title={`Remove ${u.name}`}>
                                <i className="fa fa-times"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button className="w3-button w3-theme w3-round" onClick={saveQuickTeamDetails} disabled={quickTeamSaving}>
                  <i className="fa fa-save w3-margin-right"></i>{quickTeamSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Nav */}
      <div className="w3-top">
        <div className="w3-bar w3-theme-d3" style={{ padding: '0 16px', minHeight: '50px', display: 'flex', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
          {/* First, at the far left where a back button is looked for. After
              the brand it followed the brand's flex-grow and ended up
              wherever the brand stopped — the middle of a wide screen. */}
          {view !== 'home' && view !== 'login' && (
            <button className="w3-bar-item w3-button header-back-btn"
              title="Back" aria-label="Back"
              onClick={() => {
                if (view === 'clubAnalytics') setView('clubStats');
                else if (view === 'help') setView(previousView === 'help' ? 'dashboard' : previousView);
                else if (view === 'competitionRules') {
                  if (rulesPickerContext === 'edit') {
                    setViewTracked('competition');
                    setEditingCompSettings(true);
                  } else {
                    setViewTracked('newComp');
                  }
                }
                else if (view === 'playerProfile' || view === 'teamProfile') goBack();
                else { setView('home'); setSelectedComp(null); }
              }}>
              <i className="fa fa-arrow-left"></i>
            </button>
          )}
          {/* Whose app this is, and whose club you are looking at. That is the
              header's job now — Member ID moved to the bottom bar on a phone
              and to the card on the home page everywhere else, and a second
              home button above the first was never doing anything.

              Still a button: tapping the name of the thing you are in to get
              back to its front page is an old habit and worth keeping. */}
          <button className="app-brand"
            onClick={() => { setView('home'); setSelectedComp(null); window.history.replaceState({ view: 'home' }, '', window.location.pathname); }}
            title="Home" aria-label="Home">
            {/* Inside a club, the club is the name that matters — GreenSheet
                is still in the footer. With no club chosen it is all there is. */}
            {headerClub
              ? <span className="app-brand-club">{headerClub.name}</span>
              : <span className="app-brand-name">GreenSheet</span>}
            {headerClub && headerClub.logo
              ? <img className="app-brand-logo" src={headerClub.logo} alt="" />
              : <span className="brand-strapline app-brand-strap">Bowls Competition Manager</span>}
          </button>
          {selectedComp && view === 'competition' && (
              <span className="w3-bar-item" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px 6px', fontSize: '1.05em', fontWeight: 700, color: '#fff', letterSpacing: '0.01em', minWidth: 0, flex: '1 1 auto' }}>
                {selectedComp.name}
              </span>
          )}
          {/* No breadcrumbs after the club name. A "> Chris Stone" floating in
              the middle of the bar repeated the heading of the page below it,
              and the back arrow on the left already says where you came from. */}
          <span style={{ flex: 1 }}></span>
          {/* Desktop nav buttons - hidden on small screens */}
          <span className="w3-hide-small desktop-nav" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          {/* Pending approvals notification */}
          {isAdmin && (pendingRegCount + pendingMembershipCount + pendingScoreCount) > 0 && (
            <button className="w3-button w3-round" onClick={openPendingApproval}
              title={`${pendingRegCount + pendingMembershipCount + pendingScoreCount} approval${(pendingRegCount + pendingMembershipCount + pendingScoreCount) !== 1 ? 's' : ''} pending: ${pendingRegCount} registrations, ${pendingMembershipCount} memberships, ${pendingScoreCount} game scores`}
              style={{ flexShrink: 0, background: '#ff9800', color: '#fff', margin: '0 8px', padding: '4px 14px', fontWeight: 600, fontSize: 'var(--gs-text-sm)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <i className="fa fa-bell"></i>
              <span>{pendingRegCount + pendingMembershipCount + pendingScoreCount} Approvals</span>
            </button>
          )}
          {/* The name moved to the footer: it is who you are, not somewhere
              to go, and it was sitting in the row of places to go. */}
          {isLoggedIn && (
            <button className={'w3-button w3-padding-large' + (view === 'dashboard' ? ' nav-active' : '')}
              aria-current={view === 'dashboard' ? 'page' : undefined}
              onClick={() => openOverview('overview-competitions')}
              title="Competitions" style={{ flexShrink: 0 }}>
              <i className="fa fa-trophy"></i><span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Competitions</span>
            </button>
          )}
          {isAdmin && (
            <>
              <button className={'w3-button w3-padding-large' + (view === 'clubPlayers' ? ' nav-active' : '')} aria-current={view === 'clubPlayers' ? 'page' : undefined} onClick={() => { setViewTracked('clubPlayers'); setPlayerProfile(null); }} title="Club Players" style={{ flexShrink: 0 }}>
                <i className="fa fa-users"></i><span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Players</span>
              </button>
              <button className={'w3-button w3-padding-large' + (view === 'clubTeams' ? ' nav-active' : '')} aria-current={view === 'clubTeams' ? 'page' : undefined} onClick={() => { setViewTracked('clubTeams'); setTeamProfile(null); }} title="Club Teams" style={{ flexShrink: 0 }}>
                <i className="fa fa-shield"></i><span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Teams</span>
              </button>

            </>
          )}
          {isAdmin && (
            <button className={'w3-button w3-padding-large' + (view === 'adminUsers' ? ' nav-active' : '')} aria-current={view === 'adminUsers' ? 'page' : undefined} onClick={() => setViewTracked('adminUsers')} title="Admin Panel" style={{ flexShrink: 0 }}>
              <i className="fa fa-lock"></i><span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Admin</span>
            </button>
          )}
          {isSuperAdmin && (
            <button className={'w3-button w3-padding-large' + (view === 'siteAdmin' ? ' nav-active' : '')} aria-current={view === 'siteAdmin' ? 'page' : undefined} onClick={() => setViewTracked('siteAdmin')} title="Site Administration" style={{ flexShrink: 0 }}>
              <i className="fa fa-sitemap"></i><span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Site</span>
            </button>
          )}
          {isLoggedIn ? (<>
            {/* Applying and renewing live on the Member ID page now — one
                thing in one place, and a slot back in a nav tight enough that
                its overflow was how it lost its navigation entirely. */}
            <button className="w3-button w3-padding-large" onClick={doLogout} title="Log Out" style={{ flexShrink: 0 }}>
              <i className="fa fa-sign-out"></i>
              <span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Log Out</span>
            </button>
          </>) : (
            <button className="w3-button w3-padding-large" onClick={() => setViewTracked('login')} title="Log In" style={{ flexShrink: 0 }}>
              <i className="fa fa-sign-in"></i>
              <span className="w3-margin-left" style={{ fontSize: 'var(--gs-text-sm)' }}>Log In</span>
            </button>
          )}
          </span>
          {/* Mobile hamburger button - shown only on small screens */}
          {isAdmin && (pendingRegCount + pendingMembershipCount + pendingScoreCount) > 0 && (
            <button className="w3-button w3-hide-medium w3-hide-large mobile-menu-btn" onClick={openPendingApproval}
              title="Pending approvals" aria-label="Pending approvals"
              style={{ flexShrink: 0, position: 'relative', padding: '8px 10px' }}>
              <i className="fa fa-bell" style={{ color: '#ff9800' }}></i>
              {(pendingRegCount + pendingMembershipCount + pendingScoreCount) > 0 && <span className="w3-badge w3-orange" style={{ position: 'absolute', top: 2, right: 2, fontSize: 'var(--gs-text-xs)', minWidth: 16, height: 16, lineHeight: '16px' }}>{pendingRegCount + pendingMembershipCount + pendingScoreCount}</span>}
            </button>
          )}
          {/* Signed in, the bottom bar's More holds everything this did, so
              the hamburger only appears when there is no bottom bar. */}
          {!isLoggedIn && (
          <button className="w3-button w3-padding-large w3-hide-medium w3-hide-large mobile-menu-btn" onClick={() => setShowSidebar(!showSidebar)} style={{ flexShrink: 0 }} title="Menu" aria-label="Menu">
            <i className="fa fa-bars"></i>
          </button>
          )}
        </div>
      </div>

      {/* The phone navigation bar. Only rendered when somebody is logged in:
          signed out there is one destination and a bar of six would be a lie
          about how much app there is. */}
      {isLoggedIn && (
        <MobileNav
          view={view}
          go={id => {
            if (id === 'profile') { openProfile(currentUser.id); return; }
            // Competitions already exist, on the overview. Sending people to a
            // second list of the same thing would be worse than sending them there.
            if (id === 'competitions') { openOverview('overview-competitions'); return; }
            setViewTracked(id);
          }}
          more={[
            { id: 'results', label: 'Results', icon: 'list-ol', onClick: () => openOverview('overview-results') },
            { id: 'overview', label: 'Overview', icon: 'bar-chart', onClick: () => openOverview(null) },
            { id: 'ladder', label: 'Ladder', icon: 'trophy', onClick: () => setViewTracked('clubStats') },
            ...(isAdmin ? [
              { id: 'admin', label: 'Admin', icon: 'lock', admin: true, onClick: () => setViewTracked('adminUsers') },
              ...(isSuperAdmin ? [{ id: 'site', label: 'Site', icon: 'sitemap', admin: true, onClick: () => setViewTracked('siteAdmin') }] : []),
              { id: 'players', label: 'Players', icon: 'users', admin: true, onClick: () => { setViewTracked('clubPlayers'); setPlayerProfile(null); } },
              { id: 'teams', label: 'Teams', icon: 'shield', admin: true, onClick: () => { setViewTracked('clubTeams'); setTeamProfile(null); } },
              { id: 'screen', label: 'Club screen', icon: 'desktop', admin: true, onClick: () => setViewTracked('clubScreenControl') },
              ...(pendingRegCount + pendingMembershipCount + pendingScoreCount > 0 ? [{
                id: 'approvals', label: 'Approvals', icon: 'bell', admin: true,
                badge: pendingRegCount + pendingMembershipCount + pendingScoreCount,
                onClick: openPendingApproval,
              }] : []),
            ] : []),
            { id: 'help', label: 'Help', icon: 'question-circle', onClick: () => setViewTracked('help') },
            { id: 'logout', label: 'Log out', icon: 'sign-out', onClick: doLogout },
          ]}
        />
      )}

      {/* Mobile sidebar overlay */}
      {showSidebar && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 5 }}>
          <div onClick={() => setShowSidebar(false)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.4)' }}></div>
          <div className="w3-sidebar w3-bar-block w3-card-4 w3-animate-right w3-theme-d2" style={{ display: 'block', right: 0, left: 'auto', width: '220px', top: '50px', zIndex: 6 }}>
            {isLoggedIn && (
              <div className="w3-bar-item w3-theme-d4" style={{ padding: '12px 16px', fontWeight: 600 }}>
                <i className="fa fa-user w3-margin-right"></i>{currentUser.name}
              </div>
            )}
            {isAdmin && (
              <button className="w3-bar-item w3-button" onClick={() => { setViewTracked('clubPlayers'); setPlayerProfile(null); setShowSidebar(false); }}>
                <i className="fa fa-users w3-margin-right"></i>Players
              </button>
            )}
            {isAdmin && (
              <button className="w3-bar-item w3-button" onClick={() => { setViewTracked('clubTeams'); setTeamProfile(null); setShowSidebar(false); }}>
                <i className="fa fa-shield w3-margin-right"></i>Teams
              </button>
            )}
            {isAdmin && (pendingRegCount + pendingMembershipCount + pendingScoreCount) > 0 && (
              <button className="w3-bar-item w3-button" onClick={() => { openPendingApproval(); setShowSidebar(false); }}>
                <i className="fa fa-bell w3-margin-right"></i>Approvals
                {(pendingRegCount + pendingMembershipCount + pendingScoreCount) > 0 && <span className="w3-badge w3-orange w3-margin-left">{pendingRegCount + pendingMembershipCount + pendingScoreCount}</span>}
              </button>
            )}
            {isAdmin && (
              <button className="w3-bar-item w3-button" onClick={() => { setViewTracked('adminUsers'); setShowSidebar(false); }}>
                <i className="fa fa-lock w3-margin-right"></i>Admin
              </button>
            )}
              {shouldShowApplyMembershipButton && (
                <button className="w3-bar-item w3-button" onClick={() => {
                  const clubRef = getMembershipRouteRef(currentUser.ClubId);
                  window.history.pushState({}, '', `/membership/${clubRef}`);
                  setMembershipRoute({ clubRef: decodeURIComponent(clubRef), intent: 'apply' });
                  setShowSidebar(false);
                }}>
                  <i className="fa fa-id-card w3-margin-right"></i>Apply Membership
                </button>
              )}
            {shouldShowRenewButton && renewButtonIsDue && (
              <button className="w3-bar-item w3-button" onClick={() => {
                const clubRef = getMembershipRouteRef(currentUser.ClubId);
                window.history.pushState({}, '', `/membership/${clubRef}/renew`);
                setMembershipRoute({ clubRef: decodeURIComponent(clubRef), intent: 'renew' });
                setShowSidebar(false);
              }}>
                <i className="fa fa-refresh w3-margin-right"></i>Renew Membership
              </button>
            )}
            <hr style={{ margin: '4px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
            {isLoggedIn ? (
              <button className="w3-bar-item w3-button" onClick={() => { doLogout(); setShowSidebar(false); }}>
                <i className="fa fa-sign-out w3-margin-right"></i>Log Out
              </button>
            ) : (
              <button className="w3-bar-item w3-button" onClick={() => { setViewTracked('login'); setShowSidebar(false); }}>
                <i className="fa fa-sign-in w3-margin-right"></i>Log In
              </button>
            )}
          </div>
        </div>
      )}

      <div className="main-content" style={{ marginTop: '50px', minHeight: '80vh', paddingBottom: '80px' }}>

        {view === 'events' && <EventsView selectedClubId={selectedClubId} />}

        {view === 'home' && <PlayerHomeView clubs={clubs} comps={comps} currentUser={currentUser} dashLeaderboard={dashLeaderboard} isAdmin={isAdmin} myCompetitionIds={myCompetitionIds} myRegistrations={myRegistrations}
          needsMembershipAction={!!(onRenewMembership || onApplyMembership)}
          onOpenMyProfile={currentUser ? () => openProfile(currentUser.id) : null}
          openComp={openComp} openMemberIdPage={openMemberIdPage} recentMatches={recentMatches} selectedClubId={selectedClubId} setView={setViewTracked} upcomingMatches={upcomingMatches} />}

        {view === 'clubScreenControl' && isAdmin && (
          <ClubScreenControl
            clubs={clubs} comps={comps} dashLeaderboard={dashLeaderboard}
            recentMatches={recentMatches} selectedClubId={selectedClubId} upcomingMatches={upcomingMatches}
            onOpenScreen={() => {
              /* Asked for here, inside the click: a browser grants full
                 screen only in answer to one. The screen asks again when it
                 opens, for the case where this was refused. */
              const el = document.documentElement;
              if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
              setViewTracked('clubScreen');
            }} />
        )}

        {view === 'dashboard' && <DashboardView seasonStartMonth={seasonStartMonth} anchor={overviewAnchor} canCreateCompetition={canCreateCompetition} clubs={clubs} compYearFilter={compYearFilter} comps={comps} currentUser={currentUser} dashLeaderboard={dashLeaderboard} deleteComp={deleteComp} endDateTouched={endDateTouched} getEffectiveDefaults={getEffectiveDefaults} isAdmin={isAdmin} isLoggedIn={isLoggedIn} isSuperAdmin={isSuperAdmin} myRegistrations={myRegistrations} openComp={openComp} openProfile={openProfile} openTeamProfile={openTeamProfile} recentMatches={recentMatches} recentWinners={recentWinners} selectedClubId={selectedClubId} setCompForm={setCompForm} setSelectedPreset={setSelectedPreset} setShowCustomSettings={setShowCustomSettings} setView={setView} upcomingMatches={upcomingMatches} />}

        {view === 'competitionRules' && <CompetitionRulesView combinedRulesCatalog={combinedRulesCatalog} currentRulesSelection={currentRulesSelection} rulesPickerContext={rulesPickerContext} setEditingCompSettings={setEditingCompSettings} setViewTracked={setViewTracked} toggleDraftCompetitionRule={toggleDraftCompetitionRule} />}

        {view === 'clubPlayers' && <ClubPlayersView openNewProfile={openNewProfile} setUsers={setUsers} showToast={showToast} openProfile={openProfile} playerProfile={playerProfile} playerSearch={playerSearch} profileContent={playerProfileEl} setPlayerProfile={setPlayerProfile} setPlayerSearch={setPlayerSearch} setView={setView} users={users} />}

        {view === 'clubTeams' && <ClubTeamsView clubTeams={clubTeams} openNewTeamProfile={openNewTeamProfile} openTeamProfile={openTeamProfile} profileContent={teamProfileEl} setTeamProfile={setTeamProfile} setTeamSearch={setTeamSearch} setView={setView} teamProfile={teamProfile} teamSearch={teamSearch} teams={teams} />}

        {view === 'newComp' && <NewCompView canCreateCompetition={canCreateCompetition} getEffectiveDefaults={getEffectiveDefaults} clubs={clubs} compForm={compForm} createComp={createComp} currentUser={currentUser} customTemplates={customTemplates} deleteTemplate={deleteTemplate} endDateTouched={endDateTouched} isSuperAdmin={isSuperAdmin} openCompetitionRulesPicker={openCompetitionRulesPicker} restoreSiteDefaults={restoreSiteDefaults} saveAsTemplate={saveAsTemplate} saveClubDefaults={saveClubDefaults} selectedClubId={selectedClubId} selectedPreset={selectedPreset} setCompForm={setCompForm} setSelectedPreset={setSelectedPreset} setShowCustomSettings={setShowCustomSettings} showCustomSettings={showCustomSettings} />}

        {/* ============================================================
            CLUB STATS — full leaderboard
            ============================================================ */}
        {view === 'clubStats' && (
          <div className="page-container-md">

            <h2 style={{ marginBottom: '4px' }}><i className="fa fa-list-ol w3-text-theme w3-margin-right"></i>Club Leaderboard</h2>
            <p className="w3-text-grey" style={{ marginTop: 0 }}>All-time standings across all competitions</p>

            <div style={{ marginBottom: 12 }}>
              <button className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => setView('clubAnalytics')}>
                <i className="fa fa-bar-chart w3-margin-right"></i>Analytics Dashboard
              </button>
            </div>

            <ClubLeaderboardTable seasonStartMonth={seasonStartMonth} dashLeaderboard={dashLeaderboard} openProfile={openProfile} openTeamProfile={openTeamProfile} comps={comps} compYearFilter={compYearFilter} setCompYearFilter={setCompYearFilter} />
          </div>
        )}

        {/* ============================================================
            CLUB ANALYTICS — dashboard for all-time stats
            ============================================================ */}
        {view === 'clubAnalytics' && (
          <div className="page-container-full">

            <div className="analytics-header-row">
              <div>
                <h2 style={{ marginBottom: '4px', marginTop: '8px' }}><i className="fa fa-bar-chart w3-text-theme w3-margin-right"></i>Club Analytics</h2>
                <p className="w3-text-grey" style={{ marginTop: 0, marginBottom: 0 }}>Performance insights across all competitions</p>
              </div>
            </div>

            <ClubAnalyticsPanel seasonStartMonth={seasonStartMonth} dashLeaderboard={dashLeaderboard} prevYearLeaderboard={prevYearLeaderboard} comps={comps} compYearFilter={compYearFilter} />
          </div>
        )}

        {view === 'memberId' && currentUser && <MemberIdView clubs={clubs} currentUser={currentUser} memberQrData={memberQrData} memberQrLoading={memberQrLoading} onApplyMembership={onApplyMembership} onRenewMembership={onRenewMembership} />}

        {/* ============================================================
            PLAYER PROFILE (standalone — reached from competition etc.)
          ============================================================ */}
        {view === 'playerProfile' && playerProfile && (
          <div className="page-container-md" style={{ paddingTop: 'var(--gs-space-4)' }}>
            {/* Adding a player belongs on the Players list, not above somebody's profile. */}
            {playerProfileEl}
          </div>
        )}

        {/* ============================================================
            TEAM PROFILE (standalone — reached from competition etc.)
            ============================================================ */}
        {view === 'teamProfile' && teamProfile && (
          <div className="page-container-md">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '16px 0' }}>
              <button className="w3-button w3-theme-l2 w3-round" onClick={openNewTeamProfile}>
                <i className="fa fa-plus w3-margin-right"></i>New Team
              </button>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0 }}>{teamProfile.id ? teamProfile.name : 'New Team'}</h2>
                {!teamProfile.id && <span className="w3-text-grey">Fill in the details below…</span>}
                {teamProfile.id && <span className="w3-text-grey w3-small">Created {new Date(teamProfile.createdAt).toLocaleDateString()}</span>}
              </div>
            </div>
            {teamProfileEl}
          </div>
        )}

        {/* ============================================================
            COMPETITION VIEW (full-width, step-guided)
            ============================================================ */}
        {view === 'competition' && selectedComp && (
          <div className="page-container-lg">

            {/* Admin registration buttons (floating) */}
            {isAdmin && selectedComp && (
              <div style={{ margin: '8px 0', display: 'flex', justifyContent: 'flex-end' }}>
                <AdminCompRegistrationBar compId={selectedComp.id} onApproved={refreshComp} onRegChange={refreshPendingCounts} externalShowRegs={showCompRegs} onCloseRegs={() => setShowCompRegs(false)} showToast={showToast} />
              </div>
            )}

            {/* Edit Competition Settings Modal */}
            {editingCompSettings && (
              <div className="w3-modal" style={{ display: 'block' }}>
                <div ref={settingsModalRef} className="w3-modal-content w3-card-4 w3-round-large w3-animate-top" style={{ maxWidth: '920px', display: 'flex', flexDirection: 'column', margin: '0 auto', width: 'calc(100% - 20px)', maxHeight: 'calc(100dvh - 16px)', transform: `translate(${settingsPos.x}px, ${settingsPos.y}px)` }}>
                  <header className="w3-container w3-theme" style={{ borderRadius: '8px 8px 0 0', padding: '6px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, cursor: 'grab', userSelect: 'none' }}
                    onMouseDown={onSettingsMouseDown}>
                    <h5 style={{ margin: 0, fontSize: 'var(--gs-text-sm)' }}><i className="fa fa-cog w3-margin-right"></i>Competition Settings</h5>
                    <button className="w3-button w3-round w3-hover-theme-l3" style={{ padding: '2px 10px' }} onClick={() => setEditingCompSettings(false)}>&times;</button>
                  </header>
                  <div style={{ padding: '8px 12px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                    <Section label="The basics">
                      <div className="gs-field-grid" style={{ '--gs-cols': '2fr 1fr 1fr 1fr' }}>
                        <Field label="Competition name" htmlFor="comp-edit-name">
                          <input id="comp-edit-name" className="w3-input" value={compEditForm.name}
                            onChange={e => setCompEditForm(f => ({ ...f, name: e.target.value }))} />
                        </Field>
                        <Field label="Starts" htmlFor="comp-edit-start">
                          <input id="comp-edit-start" className="w3-input" type="date"
                            value={compEditForm.startDate || ''}
                            onChange={e => setCompEditForm(f => ({ ...f, startDate: e.target.value }))} />
                        </Field>
                        <Field label="Ends" htmlFor="comp-edit-end">
                          <input id="comp-edit-end" className="w3-input" type="date"
                            value={compEditForm.endDate || ''}
                            onChange={e => setCompEditForm(f => ({ ...f, endDate: e.target.value }))} />
                        </Field>
                        <Field label="Entry fee ($)" htmlFor="comp-edit-fee">
                          <input id="comp-edit-fee" className="w3-input" type="number" min="0" step="0.01"
                            value={compEditForm.entryFee ?? ''}
                            onChange={e => {
                              const v = e.target.value;
                              setCompEditForm(f => ({ ...f, entryFee: v === '' ? '' : parseFloat(v) }));
                            }} />
                        </Field>
                      </div>
                    </Section>

                    <CompetitionSettingsFields
                      form={compEditForm}
                      setForm={setCompEditForm}
                      clubs={clubs}
                      clubId={selectedComp?.ClubId || selectedClubId || (currentUser && currentUser.ClubId)}
                      variant="edit"
                      rulesAction={
                        <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)' }}
                          onClick={() => openCompetitionRulesPicker('edit')}>
                          <i className="fa fa-book w3-margin-right"></i>Competition Rules ({(compEditForm.appliedRules || []).length})
                        </button>
                      }
                      entries={
                        <div style={{ marginTop: 'var(--gs-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--gs-space-3)' }}>
                          <Toggle
                            label={compEditForm.registrationClosed ? 'Registration is closed' : 'Registration is open'}
                            checked={!compEditForm.registrationClosed}
                            onChange={v => setCompEditForm(f => ({ ...f, registrationClosed: !v }))} />
                          <div style={{ display: 'flex', gap: 'var(--gs-space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div className="w3-input" style={{ flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gs-text-muted)' }}>
                                {PUBLIC_APP_ORIGIN}/register/{selectedComp.id}
                              </span>
                              <button className="w3-button w3-round" style={{ padding: '0 8px', color: 'var(--gs-accent-text)', fontWeight: 600 }}
                                title="Copy registration link" aria-label="Copy registration link"
                                onClick={() => navigator.clipboard.writeText(`${PUBLIC_APP_ORIGIN}/register/${selectedComp.id}`)}>
                                Copy
                              </button>
                            </div>
                            <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                              onClick={() => showSettingsQR(selectedComp.id)} title="Show Registration QR">
                              <i className="fa fa-qrcode w3-margin-right"></i>QR code
                            </button>
                            <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                              onClick={() => { setEditingCompSettings(false); setShowCompRegs(true); }}>
                              <i className="fa fa-list w3-margin-right"></i>Registrations
                            </button>
                          </div>
                        </div>
                      } />

                    {settingsQrData && (
                      <div className="w3-modal" style={{ display: 'block', zIndex: 2000 }}>
                        <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '400px' }}>
                          <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700 }}>Registration QR Code</span>
                            <button className="w3-button w3-round" onClick={() => setSettingsQrData(null)}>&times;</button>
                          </div>
                          <div className="w3-container w3-padding w3-center">
                            <h4>{settingsQrData.competitionName}</h4>
                            <img src={settingsQrData.qrCode} alt="QR Code" style={{ width: '280px', height: '280px' }} />
                            <p className="w3-small w3-text-grey" style={{ wordBreak: 'break-all' }}>{settingsQrData.url}</p>
                            <button className="w3-button w3-theme w3-round w3-margin-bottom"
                              onClick={() => navigator.clipboard.writeText(settingsQrData.url)}>
                              <i className="fa fa-copy w3-margin-right"></i>Copy Link
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '6px 12px', display: 'flex', gap: '6px', flexShrink: 0, borderTop: '1px solid #e0e0e0', borderRadius: '0 0 8px 8px' }}>
                      <button className="w3-button w3-theme w3-round-large w3-small" style={{ flex: 1 }} onClick={saveCompSettings}>
                        <i className="fa fa-check w3-margin-right"></i>Save
                      </button>
                      <button className="w3-button w3-light-grey w3-round-large w3-small" style={{ flex: 1 }} onClick={() => setEditingCompSettings(false)}>
                        Cancel
                      </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step indicator bar */}
            <div className="step-bar">
              {STEPS.filter(s => {
                // Hide format-inapplicable steps
                if (s.key === 'teams' && selectedComp.format === 'singles') return false;
                if (s.key === 'players' && selectedComp.format !== 'singles' && selectedComp.teamAssignment !== 'auto-random' && selectedComp.teamAssignment !== 'auto-rated') return false;
                // Non-admin: only show Home, Scores, Leaderboard
                if (!isAdmin && ['players', 'teams', 'fixtures'].includes(s.key)) return false;
                return true;
              }).map((step, i) => {
                const isActive = activeStep === step.key;
                const isDone =
                  (step.key === 'home' && true) ||
                  (step.key === 'players' && compPlayers.length > 0) ||
                  (step.key === 'teams' && teams.length > 0) ||
                  (step.key === 'fixtures' && matches.length > 0) ||
                  (step.key === 'scores' && completedMatches > 0) ||
                  (step.key === 'leaderboard' && completedMatches > 0);
                return (
                  <button key={step.key}
                    className={`step-item ${isActive ? 'step-active' : ''} ${isDone && !isActive ? 'step-done' : ''}`}
                    onClick={() => setActiveStep(step.key)}>
                    {step.key === 'leaderboard' && !(isDone && !isActive) ? <TrophySvg size={18} /> : <i className={`fa ${isDone && !isActive && step.key !== 'home' ? 'fa-check-circle' : step.icon}`}></i>}
                    <span className="step-label">{step.label}</span>
                  </button>
                );
              })}
              <button className="step-item step-slideshow" onClick={() => setShowSlideshow(true)} title="Play the competition screens in turn on a display">
                <i className="fa fa-television"></i>
                <span className="step-label">Slideshow</span>
              </button>
            </div>

            {showSlideshow && (
              <CompetitionSlideshow
                matches={matches}
                onClose={() => setShowSlideshow(false)}
                selectedComp={selectedComp}
                screens={[
                  { key: 'home', label: 'Home' },
                  { key: 'teams', label: selectedComp.format === 'singles' ? 'Players' : 'Teams' },
                  { key: 'fixtures', label: 'Draw' },
                  { key: 'scores', label: 'Scores' },
                  { key: 'leaderboard', label: 'Results' },
                  { key: 'analytics', label: 'Analytics' },
                ]}
                renderScreen={(key, shownMatches) => {
                  // Read-only versions of the tabs: admin off, everything that
                  // would change the competition a no-op.
                  const noop = () => {};
                  const shownCompleted = shownMatches.filter(m => m.completed && m.result !== 'bye' && m.result !== 'not-played').length;
                  const shownPlayable = shownMatches.filter(m => m.result !== 'bye' && m.result !== 'not-played').length;
                  if (key === 'home') return <CompetitionHome appliedRulesForHome={appliedRulesForHome} compPlayers={compPlayers} competitionHomeLeaderboardMinimized={false} completedMatches={completedMatches} currentUser={currentUser} displayRulesForHome={displayRulesForHome} handleCompTeamClick={noop} homeRuleDetail={null} homeRuleTitle="" isAdmin={false} isFinalised={isFinalised} isLoggedIn={false} leaderboard={leaderboard} matches={matches} openCompSettings={noop} openCompetitionRulesPicker={noop} printCompetitionRules={noop} refreshComp={noop} saveCompetitionAppliedRules={noop} savingHomeRules={false} selectedComp={selectedComp} setActiveStep={noop} setCompetitionHomeLeaderboardMinimized={noop} setHomeRuleDetail={noop} setHomeRuleTitle={noop} setScoringMatch={noop} setShowRulesPreviewModal={noop} showRulesPreviewModal={false} teams={teams} totalPlayable={totalPlayable} onRegistered={noop} />;
                  if (key === 'teams') return <CompetitionTeams addPlayerToTeam={noop} assignedPlayerIds={assignedPlayerIds} availableForTeam={availableForTeam} clubTeams={clubTeams} compPlayers={compPlayers} createTeam={noop} deleteTeam={noop} editingTeam={null} generateTeams={noop} importClubTeam={noop} importableClubTeams={[]} isAdmin={false} isFinalised={isFinalised} isLocked={true} newTeamName="" openTeamProfile={noop} removePlayerFromTeam={noop} renameTeam={noop} selectedComp={selectedComp} selectedCompDiscipline={selectedCompDiscipline} setActiveStep={noop} setEditingTeam={noop} setNewTeamName={noop} setTeamPlayerPick={noop} teamPlayerPick="" teams={teams} togglePaid={noop} users={users} emailPanel={null} />;
                  if (key === 'fixtures') return <CompetitionFixtures addManualMatch={noop} clearScore={noop} completedMatches={shownCompleted} deleteDraw={noop} deleteMatch={noop} fixtureMode="auto" generateFinals={noop} generateFixtures={noop} handleCompTeamClick={noop} handleManualDrawClick={noop} isAdmin={false} isFinalised={isFinalised} isLocked={true} isReopened={isReopened} manualMatch={manualMatch} manualTimeLocked={manualTimeLocked} matches={shownMatches} selectedComp={selectedComp} setActiveStep={noop} setComps={noop} setFixtureMode={noop} setManualMatch={noop} setScoringMatch={noop} setSelectedComp={noop} showConfirm={noop} teams={teams} />;
                  if (key === 'scores') return <CompetitionScores approveScore={noop} completedMatches={shownCompleted} isAdmin={false} isFinalised={isFinalised} isPlayerInMatch={() => false} leaderboard={leaderboard} matches={shownMatches} refreshComp={noop} selectedComp={selectedComp} setActiveStep={noop} setScoringMatch={noop} showConfirm={noop} totalPlayable={shownPlayable} />;
                  if (key === 'leaderboard') return <LeaderboardPanel leaderboard={leaderboard} selectedComp={selectedComp} isAdmin={false} completedMatches={completedMatches} totalPlayable={totalPlayable} refreshComp={noop} handleCompTeamClick={noop} setActiveStep={noop} showToast={noop} setSelectedComp={noop} setComps={noop} />;
                  if (key === 'analytics') return <AnalyticsPanel leaderboard={leaderboard} matches={matches} selectedComp={selectedComp} setActiveStep={noop} refreshComp={noop} />;
                  return null;
                }}
              />
            )}

            {activeStep === 'home' && <CompetitionHome onRegistered={refreshMyRegistrations} appliedRulesForHome={appliedRulesForHome} compPlayers={compPlayers} competitionHomeLeaderboardMinimized={competitionHomeLeaderboardMinimized} completedMatches={completedMatches} currentUser={currentUser} displayRulesForHome={displayRulesForHome} handleCompTeamClick={handleCompTeamClick} homeRuleDetail={homeRuleDetail} homeRuleTitle={homeRuleTitle} isAdmin={isAdmin} isFinalised={isFinalised} isLoggedIn={isLoggedIn} leaderboard={leaderboard} matches={matches} openCompSettings={openCompSettings} openCompetitionRulesPicker={openCompetitionRulesPicker} printCompetitionRules={printCompetitionRules} refreshComp={refreshComp} saveCompetitionAppliedRules={saveCompetitionAppliedRules} savingHomeRules={savingHomeRules} selectedComp={selectedComp} setActiveStep={setActiveStep} setCompetitionHomeLeaderboardMinimized={setCompetitionHomeLeaderboardMinimized} setHomeRuleDetail={setHomeRuleDetail} setHomeRuleTitle={setHomeRuleTitle} setScoringMatch={setScoringMatch} setShowRulesPreviewModal={setShowRulesPreviewModal} showRulesPreviewModal={showRulesPreviewModal} teams={teams} totalPlayable={totalPlayable} />}

            {activeStep === 'players' && <CompetitionPlayers addAllPlayers={addAllPlayers} allEmailPlayersSelected={allEmailPlayersSelected} compPlayers={compPlayers} competitionPlayersWithEmail={competitionPlayersWithEmail} existingPlayerId={existingPlayerId} isAdmin={isAdmin} isFinalised={isFinalised} isLocked={isLocked} openProfile={openProfile} quickAddPlayer={quickAddPlayer} quickName={quickName} quickRef={quickRef} removePlayer={removePlayer} selectedComp={selectedComp} selectedCompPlayerIds={selectedCompPlayerIds} setActiveStep={setActiveStep} setCompPlayers={setCompPlayers} setExistingPlayerId={setExistingPlayerId} setMatches={setMatches} setQuickName={setQuickName} setSelectedCompPlayerIds={setSelectedCompPlayerIds} setTeams={setTeams} showConfirm={showConfirm} showToast={showToast} teams={teams} togglePaid={togglePaid} users={users}  playerEmailPanel={<CompetitionPlayerEmailPanel compEmailMode={compEmailMode} compGeneralSendMode={compGeneralSendMode} compGeneralTemplateId={compGeneralTemplateId} compQuickEmailBody={compQuickEmailBody} compQuickEmailSubject={compQuickEmailSubject} hasEntryConfirmationTemplate={hasEntryConfirmationTemplate} isAdmin={isAdmin} manualCompEmailTemplates={manualCompEmailTemplates} selectedCompPlayerIds={selectedCompPlayerIds} sendCompetitionEmail={sendCompetitionEmail} sendingCompEmail={sendingCompEmail} setCompEmailMode={setCompEmailMode} setCompGeneralSendMode={setCompGeneralSendMode} setCompGeneralTemplateId={setCompGeneralTemplateId} setCompQuickEmailBody={setCompQuickEmailBody} setCompQuickEmailSubject={setCompQuickEmailSubject} />} />}

            {activeStep === 'teams' && <CompetitionTeams addPlayerToTeam={addPlayerToTeam} assignedPlayerIds={assignedPlayerIds} availableForTeam={availableForTeam} clubTeams={clubTeams} compPlayers={compPlayers} createTeam={createTeam} deleteTeam={deleteTeam} editingTeam={editingTeam} generateTeams={generateTeams} importClubTeam={importClubTeam} importableClubTeams={importableClubTeams} isAdmin={isAdmin} isFinalised={isFinalised} isLocked={isLocked} newTeamName={newTeamName} openTeamProfile={openTeamProfile} removePlayerFromTeam={removePlayerFromTeam} renameTeam={renameTeam} selectedComp={selectedComp} selectedCompDiscipline={selectedCompDiscipline} setActiveStep={setActiveStep} setEditingTeam={setEditingTeam} setNewTeamName={setNewTeamName} setTeamPlayerPick={setTeamPlayerPick} teamPlayerPick={teamPlayerPick} teams={teams} togglePaid={togglePaid} users={users}  emailPanel={<CompetitionEmailPanel allEmailPlayersSelected={allEmailPlayersSelected} compEmailMode={compEmailMode} compGeneralSendMode={compGeneralSendMode} compGeneralTemplateId={compGeneralTemplateId} compQuickEmailBody={compQuickEmailBody} compQuickEmailSubject={compQuickEmailSubject} competitionPlayersWithEmail={competitionPlayersWithEmail} emailSelectablePlayers={emailSelectablePlayers} hasEntryConfirmationTemplate={hasEntryConfirmationTemplate} isAdmin={isAdmin} manualCompEmailTemplates={manualCompEmailTemplates} openProfile={openProfile} selectedComp={selectedComp} selectedCompPlayerIds={selectedCompPlayerIds} sendCompetitionEmail={sendCompetitionEmail} sendingCompEmail={sendingCompEmail} setCompEmailMode={setCompEmailMode} setCompGeneralSendMode={setCompGeneralSendMode} setCompGeneralTemplateId={setCompGeneralTemplateId} setCompQuickEmailBody={setCompQuickEmailBody} setCompQuickEmailSubject={setCompQuickEmailSubject} setSelectedCompPlayerIds={setSelectedCompPlayerIds} users={users} />} />}

            {activeStep === 'fixtures' && <CompetitionFixtures addManualMatch={addManualMatch} clearScore={clearScore} completedMatches={completedMatches} deleteDraw={deleteDraw} deleteMatch={deleteMatch} fixtureMode={fixtureMode} generateFinals={generateFinals} generateFixtures={generateFixtures} handleCompTeamClick={handleCompTeamClick} handleManualDrawClick={handleManualDrawClick} isAdmin={isAdmin} isFinalised={isFinalised} isLocked={isLocked} isReopened={isReopened} manualMatch={manualMatch} manualTimeLocked={manualTimeLocked} matches={matches} selectedComp={selectedComp} setActiveStep={setActiveStep} setComps={setComps} setFixtureMode={setFixtureMode} setManualMatch={setManualMatch} setScoringMatch={setScoringMatch} setSelectedComp={setSelectedComp} showConfirm={showConfirm} teams={teams} />}

            {activeStep === 'scores' && <CompetitionScores approveScore={approveScore} completedMatches={completedMatches} isAdmin={isAdmin} isFinalised={isFinalised} isPlayerInMatch={isPlayerInMatch} leaderboard={leaderboard} matches={matches} refreshComp={refreshComp} selectedComp={selectedComp} setActiveStep={setActiveStep} setScoringMatch={setScoringMatch} showConfirm={showConfirm} totalPlayable={totalPlayable} />}

            {/* ======================
                STEP: LEADERBOARD
                ====================== */}
            {activeStep === 'leaderboard' && (
              <LeaderboardPanel
                leaderboard={leaderboard}
                selectedComp={selectedComp}
                isAdmin={isAdmin}
                completedMatches={completedMatches}
                totalPlayable={totalPlayable}
                refreshComp={refreshComp}
                handleCompTeamClick={handleCompTeamClick}
                setActiveStep={setActiveStep}
                showToast={showToast}
                setSelectedComp={setSelectedComp}
                setComps={setComps}
              />
            )}

            {/* ======================
                STEP: ANALYTICS
                ====================== */}
            {activeStep === 'analytics' && (
              <AnalyticsPanel
                leaderboard={leaderboard}
                matches={matches}
                selectedComp={selectedComp}
                setActiveStep={setActiveStep}
                refreshComp={refreshComp}
              />
            )}
          </div>
        )}

        {view === 'help' && <HelpView isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />}

        {/* ============================================================
            LOGIN / REGISTER (inline, for existing users)
            ============================================================ */}
        {view === 'login' && (
          <div className="page-container-md">
            <AuthScreen mode={authView} onLogin={async (e, p) => { await doLogin(e, p); setView('home'); }}
              onRegister={async (n, e, p, cId, membershipData, membershipIntent) => {
                return doRegister(n, e, p, cId, membershipData, membershipIntent);
                setView('home');
              }}
              onSwitch={setAuthView} />
          </div>
        )}

        {/* ============================================================
            ADMIN — USER MANAGEMENT
            ============================================================ */}
        {view === 'adminUsers' && isAdmin && (
          <AdminUsersPanel
            API={API}
            currentUser={currentUser}
            showToast={showToast}
            showConfirm={showConfirm}
            onBack={() => setView('home')}
            openProfile={openProfile}
            selectedClubId={selectedClubId}
            allClubs={clubs}
            setAllClubs={setClubs}
            customTemplates={customTemplates}
            setCustomTemplates={setCustomTemplates}
            refreshComp={refreshComp}
            siteTheme={siteTheme}
            setSiteTheme={setSiteTheme}
            onRulesLibraryChange={(generic, custom) => setRulesLibrary({ generic, custom })}
            onNewCompetition={startNewCompOn}
          />
        )}

        {/* Site administration: clubs, everyone across all of them, and the
            defaults a club inherits. Same panel, other scope — the machinery
            underneath is shared, so only the navigation splits.

            The site defaults were always here, but only a super-admin who
            thought to switch to "All Clubs" and then open Club settings ever
            found them. A page of their own is the point. */}
        {view === 'siteAdmin' && isSuperAdmin && (
          <AdminUsersPanel
            API={API}
            scope="site"
            currentUser={currentUser}
            showToast={showToast}
            showConfirm={showConfirm}
            onBack={() => setView('home')}
            openProfile={openProfile}
            selectedClubId={selectedClubId}
            allClubs={clubs}
            setAllClubs={setClubs}
            customTemplates={customTemplates}
            setCustomTemplates={setCustomTemplates}
            refreshComp={refreshComp}
            siteTheme={siteTheme}
            setSiteTheme={setSiteTheme}
            onRulesLibraryChange={(generic, custom) => setRulesLibrary({ generic, custom })}
            onNewCompetition={startNewCompOn}
          />
        )}

        {/* ============================================================
            COMPETITION REGISTRATION (from QR / direct link)
            ============================================================ */}
        {regCompId && isLoggedIn && view === 'home' && (
          <CompRegistrationFlow
            API={API}
            compId={regCompId}
            currentUser={currentUser}
            onDone={() => { setRegCompId(null); window.history.replaceState({}, '', '/'); }}
            onRegistered={refreshMyRegistrations}
          />
        )}

      </div>

      {/* Score Entry Modal */}
      {scoringMatch && (
        <ScoreModal match={scoringMatch} comp={selectedComp}
          onClose={() => setScoringMatch(null)}
          onSubmit={isAdmin ? submitScore : submitPlayerScore}
          isAdmin={isAdmin}
          canSubmit={isAdmin ? !isFinalised : (!isFinalised && isPlayerInMatch(scoringMatch))} />
      )}

      {/* Footer */}
      <footer className="app-footer w3-theme-d3" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 3, padding: '6px 0' }}>
        {/* Spans the page, because the page does. The footer used to cap
            itself at 1800px and centre that, a number it had chosen alone —
            so once the content ran wider, the brand floated in from the left
            attached to nothing. Same padding as .content-full, so the brand
            lands under the first column at any width. */}
        <div style={{ margin: '0 auto', padding: '0 16px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
          {/* flexWrap matters: with the club and year filters both showing, this
              row is wider than a 390px phone, and nowrap pushed 6px of it off
              the side of the page rather than reflowing. */}
          <span className="w3-small" style={{ fontWeight: 600, display: 'flex', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, gap: '8px' }}><img src="/icon.svg" alt="" style={{ width: '20px', height: '20px' }} />GreenSheet <span className="brand-strapline" style={{ fontSize: 'var(--gs-text-sm)', fontWeight: 400, opacity: 0.7 }}>Bowls Competition Manager</span>
            {isSuperAdmin && clubs.length > 1 && (
              <select
                className="w3-select w3-border w3-round"
                style={{ width: 'auto', maxWidth: '180px', padding: '2px 6px', fontSize: 'var(--gs-text-sm)', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                value={selectedClubId}
                onChange={e => setSelectedClubId(e.target.value)}
                title="Filter by club">
                <option value="" style={{ color: '#333' }}>All Clubs</option>
                {clubs.map(c => <option key={c.id} value={c.id} style={{ color: '#333' }}>{c.name}</option>)}
              </select>
            )}
            {(() => {
              const years = [...new Set(comps.map(c => seasonOf(c.startDate, seasonStartMonth)).filter(y => y !== null))].sort().reverse();
              return years.length > 1 ? (
                <select
                  className="w3-select w3-border w3-round"
                  style={{ width: 'auto', maxWidth: '120px', padding: '2px 6px', fontSize: 'var(--gs-text-sm)', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                  value={compYearFilter}
                  onChange={e => setCompYearFilter(e.target.value)}
                  title={seasonStartMonth === 1 ? 'Filter by year' : `Filter by season (${MONTH_NAMES[seasonStartMonth - 1]} to ${MONTH_NAMES[(seasonStartMonth + 10) % 12]})`}>
                  <option value="all" style={{ color: '#333' }}>{seasonStartMonth === 1 ? 'All Years' : 'All Seasons'}</option>
                  {years.map(y => <option key={y} value={y} style={{ color: '#333' }}>{seasonLabel(y, seasonStartMonth)}</option>)}
                </select>
              ) : null;
            })()}
          </span>
          {/* Four of these pointed at documents Bowls NZ has since moved, and a
              dead link in a footer is worse than no link — it teaches people
              not to trust the ones that do work. Help and Privacy remain,
              plus the club's own site when it has one. */}
          <span className="footer-links w3-tiny" style={{ opacity: 0.85 }}>
            {isLoggedIn && (
              <>
                <span className="footer-user">{currentUser.name}</span>
                <span className="footer-sep" style={{ margin: '0 6px' }}>&bull;</span>
              </>
            )}
            <span onClick={() => setViewTracked('help')} style={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer' }}>Help</span>
            <span className="footer-sep" style={{ margin: '0 6px' }}>&bull;</span>
            <a href="/privacy.html" target="_blank" rel="noopener" style={{ color: '#fff', textDecoration: 'underline' }}>Privacy</a>
            <span className="footer-sep" style={{ margin: '0 6px' }}>&bull;</span>
            <a href="/terms.html" target="_blank" rel="noopener" style={{ color: '#fff', textDecoration: 'underline' }}>Terms</a>
            {clubWebsiteHref && (
              <>
                <span className="footer-sep" style={{ margin: '0 6px' }}>&bull;</span>
                <a href={clubWebsiteHref} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#fff', textDecoration: 'underline' }}>
                  {headerClub.name}
                </a>
              </>
            )}
          </span>
        </div>
      </footer>
    </div>
  );
}



















/* ====================================================================
   COMP HEADER QR — Scannable QR code in the competition header
   ==================================================================== */


export default App;
